import { getState, setState, setSilent, showToast, CONFIG, totalUSDFor, toReporting, fmtReporting, filteredTransactions, isGaslessCapable, swapDeliverAvailable } from './store.js';
import { walletCall } from './lib/wallet.js';
import { haptic } from './lib/haptic.js';
import {
  generateVaultKey, createPinWrap, unwrapWithPin,
  platformAuthenticatorAvailable, registerWebauthn, unwrapWithWebauthn,
  encryptJSON, decryptJSON, setSessionVault, getSessionVault, clearSessionVault
} from './lib/vault.js';
import { buildStatementPdf } from './lib/pdf.js';
import { isValidAddressForChain, isValidEvmAddress, isValidTronAddress } from './lib/chainAddress.js';
import { isPositiveDecimalString } from './lib/decimal.js';
// Aliased away from the bare name `t` — this file already uses `t` as the
// loop variable for individual transactions/transfers throughout (see
// loadTransactions, exportCSV/exportPDF below), and shadowing the
// translator inside those scopes would silently make it uncallable there.
import { t as translate, setLanguage as i18nSetLanguage } from './i18n/index.js';

const { CHAINS, NET } = CONFIG;
const CLIPBOARD_CLEAR_MS = 30000;
let clipboardTimer = null;

// feature-harden.md 6.2's visibilitychange auto-lock (main.js) has a real
// interaction with WebAuthn: navigator.credentials.create()/get() shows a
// native platform-auth prompt (Touch ID sheet on macOS, passkey dialog on
// desktop Chrome, system sheet on iOS), and on several real platforms that
// prompt fires visibilitychange with document.hidden === true while it's
// up — the page genuinely loses visibility, not just focus. Without this
// guard, main.js's listener would call goLock() mid-prompt, tearing down
// the very session unlockWithWebauthn()/confirmRevealWithWebauthn() are
// about to write into once the prompt resolves. Every WebAuthn-invoking
// call site below sets this before the call and clears it in a finally —
// vault.js's own WEBAUTHN_TIMEOUT_MS already bounds how long a prompt (and
// therefore this flag) can stay open. Exported as a getter, not the raw
// mutable value, so main.js can only read it, matching how it already
// reads autoLockMsFor rather than reaching into actions.js internals.
let webauthnPromptActive = false;
export function isWebauthnPromptActive() {
  return webauthnPromptActive;
}

export function go(screen) {
  setState({ screen });
}

/* ---- Desktop (feature-ui.md's Desktop section) ------------------------- */
export function toggleSidebar() {
  const collapsed = !getState().sidebarCollapsed;
  try { localStorage.setItem('tynti_sidebar_collapsed', collapsed ? '1' : '0'); } catch { /* storage unavailable */ }
  setState({ sidebarCollapsed: collapsed });
}

// Opens the requested screen in a new browser window/tab rather than
// navigating away from the current one (Receive/Send/Activity's desktop-only
// title icon). A hash fragment, not a query string: the fragment never
// reaches the network request the service worker sees (public/sw.js caches
// exact request URLs), so `#screen=receive` still cache-hits the same
// app-shell entry offline that `/`/`/index.html` already cover — a `?`
// query string would miss that cache on a cold/offline open. The new
// window boots its own Web Worker with no key material in it (CLAUDE.md:
// key material never leaves the worker boundary, and never persists to
// disk/localStorage) — see store.js's pendingScreen for how it lands on
// the requested screen only after that window's own unlock.
const NEW_WINDOW_SCREENS = { home: 'home', receive: 'receive', send: 'send', activity: 'activity' };
export function openInNewWindow(key) {
  if (!NEW_WINDOW_SCREENS[key]) return;
  const url = `${location.origin}${location.pathname}#screen=${key}`;
  window.open(url, '_blank', 'noopener');
}

// Reads the `#screen=X` fragment set by openInNewWindow above, once at boot
// (main.js) — cleared from the URL immediately after reading so a page
// reload later in the same window doesn't keep re-applying a stale target.
export function consumeScreenHash() {
  const m = /^#screen=(\w+)$/.exec(location.hash);
  if (m && NEW_WINDOW_SCREENS[m[1]]) {
    history.replaceState(null, '', location.pathname + location.search);
    setState({ pendingScreen: m[1] });
  }
}

// Shared by unlock/unlockWithWebauthn below: lands on whatever screen a
// desktop new-window click asked for (see openInNewWindow), or 'home'
// otherwise/by default. Deliberately not consulted from the fresh-
// onboarding path (submitOpenAccount) — a device with no profiles yet was
// never mid-session in another window, so there's nothing meaningful to
// resume there.
function applyPendingScreen() {
  const key = getState().pendingScreen;
  setState({ pendingScreen: null });
  if (key === 'receive') { goReceive(); return; }
  if (key === 'send') { goSend(); return; }
  if (key === 'activity') { setState({ screen: 'txns' }); return; }
  setState({ screen: 'home' });
}

// 'system' clears the stored override so style.css's prefers-color-scheme
// media query decides; 'light'/'dark' pin it. Applying the data-theme
// attribute to <html> itself happens in app.js's render() (that's DOM the
// virtual-DOM tree never covers, since it's outside #app).
export function setTheme(theme) {
  try {
    if (theme === 'system') localStorage.removeItem('tynti_theme');
    else localStorage.setItem('tynti_theme', theme);
  } catch { /* storage unavailable */ }
  setState({ theme });
}

// Loads the target locale's catalog (dynamic import — see src/i18n/index.js)
// before flipping state.language, so the re-render app.js's setState
// triggers already has the new catalog in memory; setting state.language
// first would re-render with t() still reading the old catalog for one
// frame. Applying <html lang>/dir happens in app.js's render(), same as
// setTheme's data-theme above.
export async function setLanguage(langCode) {
  const resolved = await i18nSetLanguage(langCode);
  try { localStorage.setItem('tynti_language', resolved); } catch { /* storage unavailable */ }
  setState({ language: resolved });
}

export function setOnline(online) {
  setState({ online });
}
export function setWorkerConnected(connected) {
  setState({ workerConnected: connected });
}
export function openStatusPanel() {
  setState({ showStatusPanel: true });
  checkChainConnectivity();
}
export function closeStatusPanel() {
  setState({ showStatusPanel: false });
}

// Real per-chain liveness (feature-backend.md item 9) — checkChainConnectivity
// in the worker is a single bounded-timeout attempt per chain, not item 2's
// infinite-retry withRetry (see tether.worker.js's comment on why), so this
// is safe to call directly from a UI-triggered path (panel open, retest
// button) without the fire-and-forget caution ensureAccountBalances/
// ensureRecentTransactions need — it always settles.
let chainConnectivityInFlight = false;
export async function checkChainConnectivity() {
  if (chainConnectivityInFlight) return;
  chainConnectivityInFlight = true;
  try {
    const result = await walletCall('checkChainConnectivity', { testnetEnabled: getState().testnetEnabled });
    setState({ chainConnectivity: result });
  } catch {
    setState({ chainConnectivity: { 'Ethereum · ERC-20': false, 'Tron · TRC-20': false } });
  } finally {
    chainConnectivityInFlight = false;
  }
}

export function toggleStatusInfo(row) {
  setState((s) => ({ statusInfoRow: s.statusInfoRow === row ? null : row }));
}

// Retest handlers for the Status panel's per-row rotateCcw icons.
export function retestOnline() {
  setState({ online: typeof navigator !== 'undefined' ? navigator.onLine : true });
}
export function retestBlockchains() {
  checkChainConnectivity();
}
export function retestFxRates() {
  ensureFxRates();
}

export function openProfileMenu() {
  setState({ showProfileMenu: true });
}
export function closeProfileMenu() {
  setState({ showProfileMenu: false });
}

// Landing screen's language picker (top-right icon on the pre-auth screens)
// — same setLanguage() below as Settings' own select, so picking a
// language here is just as sticky (persisted to tynti_language) and never
// needs rediscovering in Settings later.
export function openLanguagePicker() {
  setState({ showLanguagePicker: true });
}
export function closeLanguagePicker() {
  setState({ showLanguagePicker: false });
}
// transactions/settings are global state, not scoped per profile
// (feature-ui.md only asked for the switcher itself, not a full
// per-profile data model — a separate, larger decision left for later).
// Saved recipients and wallets (accounts) ARE per-profile (both encrypted
// under each profile's own vault key — see vault.js/persistWallets) and
// that key only exists once a profile has been unlocked, so switching
// profiles can't just swap `activeProfileId` and keep going: the
// newly-selected profile's vault key isn't in memory, and re-deriving it
// needs that profile's own PIN/biometric. Clearing the session vault and
// routing back through Landing's unlock screen is the correct behavior
// here, not just a defensive fallback — it closes what was otherwise a
// real gap (instant access to a different identity's encrypted data with
// zero re-auth).
export function switchProfile(id) {
  const s = getState();
  if (!s.profiles.some((p) => p.id === id)) return;
  try { localStorage.setItem('tynti_active_profile', id); } catch { /* storage unavailable */ }
  clearSessionVault();
  // Same reasoning as goLock: the outgoing profile's real seed must not
  // stay resident in worker memory once its session ends here.
  walletCall('clearKeys').catch(() => {});
  setState((st) => ({
    activeProfileId: id,
    showProfileMenu: false,
    screen: 'landing',
    lockCode: '', lockError: '',
    savedRecipients: [],
    wallets: [],
    privateKeys: st.privateKeys.map((k) => ({ ...k, revealed: false, words: null })),
    showVerifyBackup: false, verifyBackupKeyId: null, verifyBackupIndices: [],
    verifyBackupAnswers: ['', '', ''], verifyBackupError: ''
  }));
}
export function goAddProfile() {
  setState({ showProfileMenu: false, screen: 'landing', landingAddingProfile: true, landingFormOpen: false });
}
// Safety-net back-out for the "add profile" entry point specifically: unlike
// a brand-new device (which has nowhere else to go), this path is reached
// from an already-unlocked Home session, so getting stuck mid-form with no
// way back would be a real dead end.
export function cancelAddProfile() {
  setState({ landingAddingProfile: false, landingFormOpen: false, screen: 'home' });
}

const COPY_FLASH_MS = 1500;
let copyFlashTimer = null;

// flashKey identifies which specific copy button just fired, so its own
// checkmark/label swap can show without lighting up every copy button on
// screen — the toast alone isn't a reliable-enough affordance on its own.
export async function copyText(text, label, flashKey) {
  if (!navigator.clipboard || !navigator.clipboard.writeText) {
    showToast(translate('toast.copyFailed'));
    return;
  }
  try {
    await navigator.clipboard.writeText(text);
    haptic();
    showToast(translate('toast.copied', { label }));
    clearTimeout(clipboardTimer);
    clipboardTimer = setTimeout(() => {
      navigator.clipboard.writeText('').catch(() => {});
    }, CLIPBOARD_CLEAR_MS);
    if (flashKey) {
      setState({ copyFlash: flashKey });
      clearTimeout(copyFlashTimer);
      copyFlashTimer = setTimeout(() => setState({ copyFlash: null }), COPY_FLASH_MS);
    }
  } catch {
    showToast(translate('toast.copyFailed'));
  }
}

// Same-origin fetch of the app's own static file — not a network/WDK call
// in the sense CLAUDE.md restricts to src/workers/, just reading an asset
// this build already ships (public/llms.txt), so it can go straight in
// actions.js like copyText's other callers.
export async function copyLlmsTxt() {
  try {
    const res = await fetch('/llms.txt');
    const text = await res.text();
    await copyText(text, 'llms.txt', 'llmstxt');
  } catch {
    showToast(translate('toast.copyFailed'));
  }
}

/* ---- Open account ------------------------------------------------ */
export function openOpenAccount() {
  setState({ showOpenAccount: true, newAcctName: '', newAcctOwner: '', newAcctError: '' });
}
export function cancelOpenAccount() {
  setState({ showOpenAccount: false });
}
export function saveOpenAccount() {
  const name = getState().newAcctName.trim();
  const owner = getState().newAcctOwner.trim();
  if (!name) { setState({ newAcctError: translate('toast.enterAccountName') }); return; }
  const id = 'acct' + Date.now();
  const s = getState();
  // feature-harden.md 6.6: every account starts with a real per-transaction
  // send limit (in the reporting currency), not unlimited by default — a
  // sane default operator-tier cap the account's own controller can raise,
  // lower, or clear (null = no limit) via Home's inline editing, the same
  // way name/owner already work.
  // Exact decimal string, not a number — wallets[].balances' canonical
  // representation throughout the app (AUDIT.md F2); a freshly-opened
  // account with nothing in it yet is '0' for the same reason a real
  // fetched balance would be, not a special-cased type.
  const wallets = [...s.wallets, { id, name, owner, balances: { USDC: '0' }, sendLimit: DEFAULT_SEND_LIMIT }];
  const isFirstAccount = s.wallets.length === 0;
  setState((st) => ({
    wallets,
    showOpenAccount: false,
    // The old seeded 'ops' account meant receive.accountId/move.fromAccountId
    // always pointed at a real account from boot. Now that item 10 dropped
    // that seed, they start as '' — Receive/Send/Transfer's *render* layer
    // already falls back to `wallets[0]` when accountId doesn't match, but
    // the *action* layer (ensureReceiveAddress, onMoveConfirm, ...) reads
    // the raw state value directly with no such fallback, so it would
    // silently derive against accountId '' instead of the real account.
    // Fixing it here, once, on the account that makes wallets go from empty
    // to non-empty, restores the old guarantee — accounts are never deleted
    // in this app (see renameAccount's neighboring comment), so an id
    // assigned here never goes stale. Only the *first* account does this;
    // creating a 2nd+ account must not clobber whatever the user already
    // has selected.
    receive: isFirstAccount ? { ...st.receive, accountId: id } : st.receive,
    move: isFirstAccount ? { ...st.move, fromAccountId: id } : st.move
  }));
  persistWallets(wallets);
  showToast(translate('toast.accountOpened'));
  // ensureHomeAddresses only runs at boot (main.js) — without this, a
  // freshly-opened account has no cached receive address until the next
  // reload, so Home's per-balance explorer-link icon would silently be
  // missing for it. Real derivation (item 5's worker call), not awaited —
  // same "never block a UI action on a worker round-trip" reasoning as
  // every other ensure* call.
  ensureHomeAddresses();
  // If the Receive screen was visited before any account existed (e.g.
  // opening the first account from Receive's own empty state), it already
  // ran ensureReceiveAddress() once against the stale accountId '' and
  // cached that (wrong) address + _addrKey. Setting receive.accountId to
  // the real id above doesn't by itself invalidate that cache — nothing
  // else would ever re-derive it, so Receive would keep showing an address
  // that doesn't match the account it claims to be for. Re-running it now
  // (cache key now includes the real id, so it won't no-op) fixes that.
  if (isFirstAccount) ensureReceiveAddress();
}

// Each dimension (name/balance) toggles its own asc/desc direction when
// clicked again; clicking the other dimension's icon switches to it at that
// dimension's primary direction (A–Z, highest balance first).
export function setAccountSort(dimension) {
  setState((s) => {
    const cur = s.accountSort;
    if (dimension === 'name') {
      return { accountSort: cur === 'name-asc' ? 'name-desc' : 'name-asc' };
    }
    return { accountSort: cur === 'balance-desc' ? 'balance-asc' : 'balance-desc' };
  });
}

/* ---- Account management ------------------------------------------------ */
// Account Detail (close-account, per-currency receiving addresses) was
// removed per the Home-screen feature pass — its only entry point was the
// account card's onclick, which is now claimed by inline name/owner editing
// and per-balance-row Transfer navigation. Closing an account and viewing
// receiving addresses are intentionally unreachable for now, to be rebuilt
// on a future screen.
export function renameAccount(id, name) {
  const trimmed = name.trim();
  if (!trimmed) { showToast(translate('toast.enterAccountName')); return; }
  const wallets = getState().wallets.map((w) => (w.id === id ? { ...w, name: trimmed } : w));
  setState({ wallets });
  persistWallets(wallets);
  showToast(translate('toast.accountRenamed'));
}
export function renameAccountOwner(id, owner) {
  const wallets = getState().wallets.map((w) => (w.id === id ? { ...w, owner: owner.trim() } : w));
  setState({ wallets });
  persistWallets(wallets);
  showToast(translate('toast.ownerUpdated'));
}
// feature-harden.md 6.6: per-account send limit, expressed in the app's
// current reporting currency (same convention totalUSDFor/toReporting
// already use elsewhere) — an empty/blank draft clears the limit
// entirely (null = unlimited), anything else must be a valid positive
// decimal string or the edit is rejected outright rather than silently
// coerced.
export const DEFAULT_SEND_LIMIT = 1000;

// CCSS-KU (feature-harden.md section 2 / AUDIT.md F3): "New-recipient
// cooldown and first-payment verification (small echo test) implemented
// as default-on policies." Not user-configurable per-recipient (unlike
// sendLimit) — these are fixed, always-on defaults for every new
// recipient, matching "default-on" in the requirement's own text.
// Real-world precedent for both figures: a 24h hold before a newly-added
// payee can receive anything is a common banking/exchange convention
// (new-payee cooling-off, withdrawal-address-whitelist delays); a small
// "echo" first payment before larger amounts is the standard micro-
// deposit/test-transfer verification pattern.
export const NEW_RECIPIENT_COOLDOWN_MS = 24 * 60 * 60 * 1000;
export const ECHO_PAYMENT_LIMIT = 1; // in the current reporting currency, same convention as DEFAULT_SEND_LIMIT
export function renameAccountLimit(id, limitStr) {
  const trimmed = limitStr.trim();
  if (trimmed === '') {
    const wallets = getState().wallets.map((w) => (w.id === id ? { ...w, sendLimit: null } : w));
    setState({ wallets });
    persistWallets(wallets);
    showToast(translate('toast.sendLimitUpdated'));
    return;
  }
  if (!isPositiveDecimalString(trimmed)) { showToast(translate('errors.validAmount')); return; }
  const wallets = getState().wallets.map((w) => (w.id === id ? { ...w, sendLimit: Number(trimmed) } : w));
  setState({ wallets });
  persistWallets(wallets);
  showToast(translate('toast.sendLimitUpdated'));
}

/* ---- Home: inline name/owner editing ----------------------------------- */
function homeEditDraftFor(w, field) {
  if (field === 'name') return w.name;
  if (field === 'sendLimit') return w.sendLimit == null ? '' : String(w.sendLimit);
  return w.owner;
}
export function startHomeEdit(accountId, field) {
  const w = getState().wallets.find((w) => w.id === accountId);
  if (!w) return;
  setState({ homeEdit: { accountId, field, draft: homeEditDraftFor(w, field) } });
}
export function setHomeEditDraft(v) {
  setSilent((s) => ({ homeEdit: { ...s.homeEdit, draft: v } }));
}
export function confirmHomeEdit() {
  const { accountId, field, draft } = getState().homeEdit;
  if (field === 'name') renameAccount(accountId, draft);
  else if (field === 'owner') renameAccountOwner(accountId, draft);
  else if (field === 'sendLimit') renameAccountLimit(accountId, draft);
  setState({ homeEdit: { accountId: null, field: null, draft: '' } });
}
export function cancelHomeEdit() {
  setState({ homeEdit: { accountId: null, field: null, draft: '' } });
}
export function toggleBalanceInfo() {
  setState((s) => ({ showBalanceInfo: !s.showBalanceInfo }));
}
export function toggleActivityInfo() {
  setState((s) => ({ showActivityInfo: !s.showActivityInfo }));
}

// Derives (once) the receiving address for every account/currency pair so
// Home's per-balance explorer-link icon has something to link to. Called
// once at boot (main.js), not from render — a handful of cheap mock-worker
// calls for this app's small, static demo wallet set, not per-navigation.
export async function ensureHomeAddresses() {
  const s = getState();
  const jobs = [];
  s.wallets.forEach((w) => {
    // Every supported stablecoin (CONFIG.CHAINS), not just whichever
    // currency the account happened to be opened with (w.balances) — one
    // address receives every token on its chain, so ensureAccountBalances/
    // ensureRecentTransactions below need this cache populated for any
    // currency that might actually show up, not only the one picked at
    // creation time.
    Object.keys(CONFIG.CHAINS).forEach((ccy) => {
      const key = w.id + '|' + ccy;
      if (s.homeAddresses[key]) return;
      const chain = NET[ccy] || '';
      jobs.push(
        walletCall('deriveAddress', { accountId: w.id, ccy, chain, testnetEnabled: s.testnetEnabled, gasless: isGaslessCapable(ccy, chain) })
          .then(({ address }) => ({ key, address }))
          .catch(() => null)
      );
    });
  });
  if (!jobs.length) return;
  const results = await Promise.all(jobs);
  const patch = {};
  results.forEach((r) => { if (r) patch[r.key] = r.address; });
  if (Object.keys(patch).length) setState((st) => ({ homeAddresses: { ...st.homeAddresses, ...patch } }));
}

// Fetches real on-chain balances for every account/currency currently in
// `wallets` (the account list itself is still seeded demo data — section
// 10 removes it; this replaces the balance *values* only, per
// feature-backend.md item 5). Freshly-derived testnet addresses have no
// funds sent to them, so a real balance here legitimately reads 0 — that's
// correct, not a bug. Called post-unlock (loadWalletForSession) and after
// a transfer settles (onMoveConfirm), replacing the old fake in-memory
// arithmetic there.
let balanceRefreshInFlight = false;
// Never awaited from a critical path (unlock/account-creation/send) — see
// the two call sites below. withRetry has no retry-count ceiling by
// design (CLAUDE.md: retry until success or cancelled), so a call that
// can structurally never succeed would otherwise hang whatever awaited it
// forever, not just take a while.
export async function ensureAccountBalances() {
  if (balanceRefreshInFlight) return;
  balanceRefreshInFlight = true;
  setState((s) => ({
    wallets: s.wallets.map((w) => ({
      ...w,
      balancesLoading: Object.fromEntries(
        Object.keys(w.balances)
          .filter((ccy) => tokenAddressFor(ccy, NET[ccy]))
          .map((ccy) => [ccy, true])
      )
    }))
  }));

  const jobs = [];
  const testnetEnabled = getState().testnetEnabled;
  getState().wallets.forEach((w) => {
    // Poll every supported stablecoin (CONFIG.CHAINS), not just whichever
    // one the account happens to already be tracking (w.balances) — the
    // same address receives every token on its chain, so a currency picked
    // at account-creation time isn't the only one that can ever have real
    // funds land on it. See the merge step below for how a newly-funded
    // currency gets added to `balances` without permanently cluttering
    // every account with four always-zero rows for currencies it never
    // actually uses.
    Object.keys(CONFIG.CHAINS).forEach((ccy) => {
      const chain = NET[ccy] || '';
      const tokenAddress = tokenAddressFor(ccy, chain);
      const decimals = CONFIG.TOKEN_DECIMALS[ccy];
      // No contract at this address on the network currently in use (see
      // tokenAddressFor — e.g. Tether issues no testnet stablecoins at
      // all, confirmed against tether.to, not assumed) — nothing to fetch,
      // and retrying against a chain address with no contract deployed
      // there would never succeed. Leave that balance as-is rather than
      // spin forever or hammer a public endpoint for an impossible call.
      if (!tokenAddress || !decimals) return;
      const jobId = `bal:${w.id}:${ccy}:${Date.now()}`;
      jobs.push(
        // `balance` is already an exact decimal string (the worker's
        // getBalance formats a real BigInt base-unit amount via ethers'
        // formatUnits) — kept as-is, not parseFloat()'d into a lossy
        // float, so wallets[].balances' canonical value is never
        // precision-lossy (AUDIT.md finding F2). Number() conversion
        // happens only at display/arithmetic call sites (store.js's
        // fmtNum/toReporting), which is display formatting, not storage.
        walletCall('getBalance', { accountId: w.id, chain, tokenAddress, decimals, jobId, testnetEnabled, gasless: isGaslessCapable(ccy, chain) })
          .then(({ balance }) => ({ accountId: w.id, ccy, balance }))
          .catch(() => null)
      );
    });
  });
  const results = await Promise.all(jobs);

  setState((st) => ({
    wallets: st.wallets.map((w) => {
      const balances = { ...w.balances };
      const balancesLoading = { ...w.balancesLoading };
      Object.keys(w.balances).forEach((ccy) => { balancesLoading[ccy] = false; });
      results.forEach((r) => {
        if (!r || r.accountId !== w.id) return;
        // Already-tracked currencies always update (including back down to
        // 0, same as before this widened to all five currencies). A
        // currency this account wasn't tracking yet is only added once it
        // actually has something in it — auto-discovering real incoming
        // funds without permanently adding always-zero rows for every
        // stablecoin an account never uses.
        if (r.ccy in balances || Number(r.balance) > 0) balances[r.ccy] = r.balance;
      });
      return { ...w, balances, balancesLoading };
    })
  }));
  balanceRefreshInFlight = false;
}

// Testnet vs mainnet contract selection — a minimal, forced-early slice of
// item 11's job (full testnet/mainnet endpoint switching), pulled forward
// because item 5 can't function correctly without it: mainnet token
// contracts don't exist at those addresses on a testnet RPC (confirmed the
// hard way — see DECISIONS.md), so blindly using CONFIG.TOKEN_CONTRACTS
// against a testnet endpoint fails every call, forever, for tokens with no
// testnet deployment. Circle publishes official Sepolia testnet USDC/EURC
// contracts (verified, same two-source method as item 4). Tether publishes
// no official testnet deployment for USDT/EURT/CNHT on any network
// (confirmed against tether.to) — for USDT on Sepolia, using the widely-
// used community "Test Tether USD" contract instead (verified via
// sepolia.etherscan.io: name "Test Tether USD (USDT)", 6 decimals, before
// adding). EURT/CNHT still have no known testnet contract at all — those
// stay unavailable on testnet.
// USDT-on-Tron/Nile (2026-08-07, feature-outstanding.md's Tron-signing
// item): the Nile faucet (nileex.io) drips a real test USDT alongside test
// TRX, so — unlike EURT/CNHT — a real Nile contract does exist. Multiple
// independent "Tether USD"/USDT deployments exist on Nile testnet (not
// unusual for a testnet — nothing stops a second party redeploying the
// same mock), so this was corrected once, not guessed right the first
// time: the address originally verified here
// (`TXLAQ63Xg1NAzckPwKHvzw7CSEmLMEqcdj`, confirmed real via three live
// `triggerconstantcontract` calls) turned out not to be the one the
// user's actual faucet drip landed on. Replaced with the contract the
// user's real balance is actually on
// (`TXYZopYRdj2D9XRtbG411XZZ3kM5VkAeBf`), independently re-verified the
// same way against `nile.trongrid.io` (this app's own configured Nile
// RPC): `name() == "Tether USD"`, `symbol() == "USDT"`, `decimals() ==
// 6`, and a real 1000 USDT balance confirmed via TronGrid's
// `/v1/accounts/.../` endpoint before this address was trusted — not
// taken on a search result's word alone. See DECISIONS.md (2026-08-07).
const TESTNET_TOKEN_CONTRACTS = {
  USDC: { 'Ethereum · ERC-20': '0x1c7d4b196cb0c7b01d743fbc6116a902379c7238' },
  EURC: { 'Ethereum · ERC-20': '0x08210f9170f89ab7658f0b5e3ff39b0e03c594d4' },
  USDT: {
    'Ethereum · ERC-20': '0x7169d38820dfd117c3fa1f22a697dba58d90ba06',
    'Tron · TRC-20': 'TXYZopYRdj2D9XRtbG411XZZ3kM5VkAeBf'
  }
};
function tokenAddressFor(ccy, chain) {
  const table = getState().testnetEnabled ? TESTNET_TOKEN_CONTRACTS : CONFIG.TOKEN_CONTRACTS;
  return table[ccy] && table[ccy][chain];
}

// Fetches real on-chain transaction history for every account/currency in
// `wallets`, replacing store.js's seeded `transactions` array wholesale
// (feature-backend.md item 6) rather than merging with it — same "replace
// entirely, even if the real result is empty" precedent as
// ensureAccountBalances (item 5): freshly-derived testnet addresses with
// no real activity yet correctly show an empty Activity feed, not a mix
// of real and fabricated rows. Never awaited from a critical path, same
// reasoning as ensureAccountBalances.
let txnRefreshInFlight = false;
export async function ensureRecentTransactions() {
  if (txnRefreshInFlight) return;
  txnRefreshInFlight = true;

  const s = getState();
  const jobs = [];
  s.wallets.forEach((w) => {
    // Every supported stablecoin (CONFIG.CHAINS), not just w.balances'
    // currently-tracked currencies — same reasoning as ensureAccountBalances
    // above: this array is a full replace, not a merge, so a transfer in an
    // untracked currency needs its own fetch here to ever show up in
    // Activity at all, regardless of what `balances` happens to list.
    Object.keys(CONFIG.CHAINS).forEach((ccy) => {
      const chain = NET[ccy] || '';
      const tokenAddress = tokenAddressFor(ccy, chain);
      const decimals = CONFIG.TOKEN_DECIMALS[ccy];
      if (!tokenAddress || !decimals) return; // see tokenAddressFor — no testnet contract for this token
      const jobId = `tx:${w.id}:${ccy}:${Date.now()}`;
      jobs.push(
        walletCall('getTransactions', { accountId: w.id, chain, tokenAddress, decimals, jobId, testnetEnabled: s.testnetEnabled, gasless: isGaslessCapable(ccy, chain) })
          .then(({ transfers }) => ({ accountId: w.id, walletName: w.name, ccy, transfers }))
          .catch(() => null)
      );
    });
  });
  const results = await Promise.all(jobs);

  const transactions = [];
  results.forEach((r) => {
    if (!r) return;
    const ourAddress = (s.homeAddresses[r.accountId + '|' + r.ccy] || '').toLowerCase();
    r.transfers.forEach((t) => {
      const isOut = t.from.toLowerCase() === ourAddress;
      // t.amount is already an exact decimal string (see getBalance's
      // comment above — getTransactions' handlers format real base-unit
      // amounts the same way); prefixing the sign keeps it an exact
      // signed decimal string instead of losing precision through
      // parseFloat() at ingestion (AUDIT.md finding F2). Number() applied
      // only at display/sort/filter call sites (store.js), never stored.
      const amount = isOut ? '-' + t.amount : t.amount;
      const counterparty = isOut ? t.to : t.from;
      transactions.push({
        // A hash is only unique per chain, not per (account, direction) —
        // the same transfer can legitimately appear under two different
        // accounts' fetches (an internal transfer between two of this
        // profile's own accounts) or otherwise collide across accountIds;
        // qualify by both to keep every row's id unique in the merged list.
        id: `${t.hash}-${r.accountId}-${isOut ? 'out' : 'in'}`,
        date: new Date(t.timestampMs).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        dateISO: new Date(t.timestampMs).toISOString().slice(0, 10),
        type: isOut ? 'out' : 'in',
        accountId: r.accountId,
        // desc stays English — it's what CSV/PDF export writes verbatim
        // (see exportCSV/exportPDF below; the PDF's Standard-14 fonts
        // can't render non-Latin scripts at all, see lib/pdf.js's ascii()
        // sanitizer, so exports are English-only regardless of UI
        // language — DECISIONS.md). counterparty is kept alongside it so
        // Home/Activity's on-screen rows can build a translated
        // "Sent to {counterparty}"/"Received from {counterparty}" string
        // at render time instead (src/i18n's t()).
        desc: (isOut ? 'Sent to ' : 'Received from ') + counterparty,
        counterparty,
        wallet: r.walletName,
        ccy: r.ccy,
        amount,
        hash: t.hash
      });
    });
  });
  transactions.sort((a, b) => new Date(b.dateISO) - new Date(a.dateISO));

  setState({ transactions });
  txnRefreshInFlight = false;
}

// Fetches live frankfurter.dev rates (feature-backend.md item 7),
// replacing the old static FX table (store.js's toReporting now reads
// state.fxRates instead — see there for the peg/triangulation math).
// Public data, no key/account needed at all — called at boot (main.js),
// not gated on unlock, so it's fresh as early as possible. Frankfurter
// excludes the base currency from its own response, so USD: 1 is added
// here rather than trusting every caller to remember it. Not awaited from
// main.js's boot sequence — same "never block a critical path on a
// no-retry-ceiling call" reasoning as ensureAccountBalances/
// ensureRecentTransactions (see DECISIONS.md for the bug that taught
// this); state.fxRates' localStorage-backed initial value (store.js's
// loadFxRates) already covers the gap until this resolves.
let fxRefreshInFlight = false;
export async function ensureFxRates() {
  if (fxRefreshInFlight) return;
  fxRefreshInFlight = true;
  try {
    const jobId = `fx:${Date.now()}`;
    const { rates, date } = await walletCall('getFxRates', { jobId });
    const fxRates = { rates: { ...rates, USD: 1 }, date, fetchedAt: Date.now() };
    setState({ fxRates });
    try { localStorage.setItem('tynti_fx_rates', JSON.stringify(fxRates)); } catch { /* storage unavailable */ }
  } catch {
    // Keep whatever's already cached (possibly null, possibly stale) —
    // a failed refresh must never wipe out the last-good rate.
  } finally {
    fxRefreshInFlight = false;
  }
}

/* ---- Home: navigation to Transfer / Activity --------------------------- */
// Presets the shared `move` state (see Send, below) for an internal
// account-to-account transfer — Transfer is a restricted view onto the same
// state/actions Send uses, not a separate flow, since an internal transfer
// is already exactly the "account" branch of onMoveConfirm.
export function goTransfer(accountId, ccy) {
  setState({
    screen: 'transfer',
    move: {
      fromAccountId: accountId, ccy, chain: NET[ccy] || '', toKey: '',
      amount: '', memo: '', step: 'form', errors: {}, resultTx: null, sending: false,
      limitOverrideForAmount: null, limitOverrideForCcy: null, limitOverrideForAccountId: null,
      newRecipientOverrideForToKey: null
    }
  });
}
// Dedicated Send entry point (used by the tabbar) rather than the generic
// go('move'), so the tabbar's own intent is explicit and separable from
// go('move')'s other callers.
export function goSend() {
  setState({ screen: 'move' });
}
export function goActivityFromHome(txId) {
  setState({ screen: 'txns', txnFilter: 'all', txnAccountFilter: 'all', txnSearch: '', expandedTxId: txId });
}
// Currency-conversion "deliver as" (feature-swap.md) reset fields — a stale
// deliverCcy/quote from a previous source-currency choice must not silently
// carry forward once the source currency changes (spec's Phase 5: "Defaults
// to unset... each time the source currency changes"). Reused by every
// ccy-mutating setter (this one, setMoveField's 'ccy' case, setFromAccount,
// setSendBalance) and by setDeliverCcy itself.
const RESET_DELIVER_FIELDS = {
  deliverCcy: '', reviewSwapOut: null, reviewSwapLoading: false, reviewSwapError: null,
  reviewSwapNeedsApproval: false, reviewSwapApproveFee: null, reviewSwapApproveFeeAsset: null,
  reviewSwapFee: null, reviewSwapFeeAsset: null
};
// Resets chain to the new currency's default network, matching Receive's
// ccy-select behavior — Send doesn't need this (no chain field there).
export function setTransferCcy(ccy) {
  setState((s) => ({ move: { ...s.move, ccy, chain: (CHAINS[ccy] || [])[0] || '', ...RESET_DELIVER_FIELDS, errors: { ...s.move.errors, ccy: null } } }));
}

/* ---- Recipient management ---------------------------------------------- */
// Encrypted at rest under the active profile's vault key, keyed by profile
// id so each profile's recipients are only ever readable under that
// profile's own key (see switchProfile's comment on why profile switches
// now force a re-unlock). A missing vault key here means no profile is
// currently unlocked — recipients UI is never reachable in that state, so
// this is a silent no-op, not a real failure.
async function persistRecipients(list) {
  const { profileId, vaultKey } = getSessionVault();
  if (!vaultKey) return;
  try {
    const envelope = await encryptJSON(vaultKey, list);
    localStorage.setItem('tynti_recipients_enc_' + profileId, JSON.stringify(envelope));
  } catch { /* storage unavailable */ }
}

// Called once right after a successful unlock/onboarding (unlock,
// unlockWithWebauthn, submitOpenAccount) — never from render, and never
// synchronously at module load, since decryption needs the vault key that
// only exists post-unlock. Fails closed (empty list) on any error —
// corrupt data or a stale/mismatched key must never surface as a crash or,
// worse, someone else's recipients.
async function loadRecipientsForSession() {
  const { profileId, vaultKey } = getSessionVault();
  if (!vaultKey) { setState({ savedRecipients: [] }); return; }
  try {
    const raw = localStorage.getItem('tynti_recipients_enc_' + profileId);
    if (!raw) { setState({ savedRecipients: [] }); return; }
    const list = await decryptJSON(vaultKey, JSON.parse(raw));
    setState({ savedRecipients: Array.isArray(list) ? list : [] });
  } catch {
    setState({ savedRecipients: [] });
  }
}

// Accounts (`wallets`) mirror savedRecipients' persistence exactly, and for
// the same reason they can't be a single global localStorage key the way
// profiles/settings/fxRates are: accountIndex(accountId) in
// tether.worker.js hashes only the free-text label, with no per-profile
// salt, so two profiles both naming an account "Payroll" would derive
// different real addresses under the same label — a global wallets list
// would let one profile see another's account names/balances. Was
// previously not persisted at all (see DECISIONS.md/store.js's now-stale
// comment) — the old seeded demo accounts masked this until item 10
// removed them; a real account created via saveOpenAccount vanished on
// every reload.
async function persistWallets(list) {
  const { profileId, vaultKey } = getSessionVault();
  if (!vaultKey) return;
  try {
    const envelope = await encryptJSON(vaultKey, list);
    localStorage.setItem('tynti_wallets_enc_' + profileId, JSON.stringify(envelope));
  } catch { /* storage unavailable */ }
}

// Called from loadWalletForSession, before ensureHomeAddresses/
// ensureAccountBalances (both iterate `wallets`) — loading after those
// would leave a freshly-reloaded account with no cached address/balance
// until something else re-triggers them. Fails closed to [] on any error,
// same as loadRecipientsForSession — corrupt data or a stale/mismatched
// key must never crash unlock or, worse, surface as another profile's
// accounts.
async function loadWalletsForSession() {
  const { profileId, vaultKey } = getSessionVault();
  if (!vaultKey) { setState({ wallets: [] }); return; }
  try {
    const raw = localStorage.getItem('tynti_wallets_enc_' + profileId);
    if (!raw) { setState({ wallets: [] }); return; }
    const list = await decryptJSON(vaultKey, JSON.parse(raw));
    setState({ wallets: Array.isArray(list) ? list : [] });
  } catch {
    setState({ wallets: [] });
  }
}

export function openEditRecipient(id) {
  const r = getState().savedRecipients.find((r) => r.id === id);
  if (!r) return;
  setState({ recipientEdit: { id: r.id, name: r.name, address: r.address }, showAddRecipient: true, newRecipError: '' });
}
export function setRecipientEditField(field, value) {
  setSilent((s) => ({ recipientEdit: { ...s.recipientEdit, [field]: value } }));
}

// feature-harden.md 6.3: deleting a recipient now requires re-auth, same
// gate as saveRecipient below — reuses the confirmReveal modal
// (src/modals/confirmReveal.js) via confirmRevealPurpose rather than a
// second modal, so there's exactly one "type your passcode to confirm"
// UI to keep correct. Actual removal happens in commitDeleteRecipient,
// only reached after revealAfterVaultCheck confirms the passcode/
// biometric.
export function deleteRecipient(id) {
  setState({
    showConfirmReveal: true, confirmRevealPurpose: 'deleteRecipient', confirmRevealPayload: { recipientId: id },
    confirmRevealCode: '', confirmRevealError: ''
  });
}
function commitDeleteRecipient(id) {
  const s = getState();
  const list = s.savedRecipients.filter((r) => r.id !== id);
  const move = s.move.toKey === 'recipient:' + id ? { ...s.move, toKey: '' } : s.move;
  setState({ savedRecipients: list, move });
  persistRecipients(list);
  showToast(translate('toast.recipientRemoved'));
}

/* ---- Receive ------------------------------------------------------- */
// ensureReceiveAddress is only ever triggered from these action functions
// (event-handler call sites) and goReceive below — never from inside a
// render function. Calling a setState-triggering action synchronously
// from within render() causes reentrant nested renders, since setState
// notifies subscribers (which re-render) before the outer render call
// has finished building its own output.
// Receive has no fiat/reporting-currency option at all (there is no
// on-chain address for a currency that isn't a real chain asset) — every
// selectable ccy here is always crypto, unlike Send/Transfer's ccy select
// which legitimately includes the reporting currency as a spend-from
// option. Switching accounts resets to the same Settings-configured
// default (defaultStablecoin/defaultChain) the receive screen itself
// initializes from (store.js), not the reporting currency.
export function setReceiveAccount(accountId) {
  setState((s) => ({ receive: { ...s.receive, accountId, ccy: s.defaultStablecoin, chain: s.defaultChain } }));
  ensureReceiveAddress();
}
export function setReceiveCcy(ccy) {
  setState((s) => ({ receive: { ...s.receive, ccy, chain: (CHAINS[ccy] || [])[0] || '' } }));
  ensureReceiveAddress();
}
export function setReceiveChain(chain) {
  setState((s) => ({ receive: { ...s.receive, chain } }));
  ensureReceiveAddress();
}
export function goReceive() {
  setState({ screen: 'receive' });
  ensureReceiveAddress();
}
export function toggleReceiveDetails() {
  setState((s) => ({ receive: { ...s.receive, showDetails: !s.receive.showDetails } }));
}
// Derives (or re-derives) the receive address via the worker and caches it
// on state.receive so the render layer stays synchronous.
// Tracks the cache key currently being fetched so a call that lands while
// one is already in flight for the same inputs is a no-op. Without this,
// calling this from a render path (or several action calls in quick
// succession) can pile up overlapping fetches that keep re-arming the
// loading state after it should have settled.
let receiveAddrInFlight = null;

// `receive.accountId` is not persisted (it lives only in the in-memory
// store, reset to '' by store.js on every fresh load/unlock) while
// `wallets` IS restored from encrypted storage — so on every session
// restore this starts out pointing at no real account. Unlike
// currentSendAccount (used by Send/Transfer), nothing here defaulted it
// to a real wallet, so `deriveAddress` was called with accountId ''
// (hashStr('') = 0 => BIP-44 index 0) instead of the real account's
// index, silently showing the user a receiving address that didn't
// match the one Home/Send actually derive and sign against for that
// account. Resolve against real wallets at every use, same fallback
// pattern as currentSendAccount, rather than trusting the stored id.
function resolveReceiveAccountId(accountId, wallets) {
  return wallets.some((w) => w.id === accountId) ? accountId : (wallets[0] || {}).id;
}

export async function ensureReceiveAddress() {
  const s = getState();
  const r = s.receive;
  const accountId = resolveReceiveAccountId(r.accountId, s.wallets);
  if (!accountId) return; // no account yet — nothing to derive
  const chain = r.chain || (CHAINS[r.ccy] || [])[0] || '';
  const cacheKey = accountId + '|' + r.ccy + '|' + chain;
  if (r._addrKey === cacheKey && r.address) return;
  if (receiveAddrInFlight === cacheKey) return;

  receiveAddrInFlight = cacheKey;
  setState((st) => ({ receive: { ...st.receive, addressLoading: true, addressError: null } }));
  let address;
  try {
    ({ address } = await walletCall('deriveAddress', { accountId, ccy: r.ccy, chain, testnetEnabled: getState().testnetEnabled, gasless: isGaslessCapable(r.ccy, chain) }));
  } catch {
    receiveAddrInFlight = null;
    // Guard against a stale response landing after the user changed inputs again.
    const cur = getState().receive;
    const curAccountId = resolveReceiveAccountId(cur.accountId, getState().wallets);
    if (curAccountId + '|' + cur.ccy + '|' + (cur.chain || chain) !== cacheKey) return;
    setState((st) => ({ receive: { ...st.receive, addressLoading: false, addressError: translate('errors.generateAddressFailed') } }));
    return;
  }
  receiveAddrInFlight = null;
  const cur = getState().receive;
  const curAccountId = resolveReceiveAccountId(cur.accountId, getState().wallets);
  if (curAccountId + '|' + cur.ccy + '|' + (cur.chain || chain) !== cacheKey) return;
  setState((st) => ({ receive: { ...st.receive, chain, address, _addrKey: cacheKey, addressLoading: false, addressError: null } }));
}

/* ---- Send / move ---------------------------------------------------- */
export function setMoveField(field, value) {
  setState((s) => ({
    move: {
      ...s.move,
      [field]: value,
      ...(field === 'ccy' ? RESET_DELIVER_FIELDS : {}),
      // toKey/ccy both feed the new-recipient cooldown/echo-test check
      // (validateMove) -- a stale error from the previous destination/
      // currency must not linger once either changes.
      errors: { ...s.move.errors, [field]: null, ...(field === 'toKey' || field === 'ccy' ? { cooldown: null } : {}) }
    }
  }));
}

// Mirrors setMoveField/setTransferCcy's own reset comment — resets deliverCcy
// (feature-swap.md), never blocked on the source currency itself: the option
// list Send/Transfer render already excludes it (Phase 5/6 UI), this is a
// defensive reset only.
export function setDeliverCcy(ccy) {
  setState((s) => ({
    move: { ...s.move, ...RESET_DELIVER_FIELDS, deliverCcy: ccy, errors: { ...s.move.errors, deliverCcy: null } }
  }));
}

// feature-harden.md 6.8: memo is a local UI note only — signTransfer's
// current call shape (fromAccountId/toKey/amount/ccy) never transmits it
// anywhere — but still capped defensively rather than left unbounded, both
// as a sane UX limit and so there's already a real cap in place the day
// memo does get wired into a real broadcast. Uses setSilent, not setState,
// same reasoning as setLockCode/setConfirmRevealCode: a full re-render on
// every keystroke would dismiss the on-screen keyboard on mobile.
export const MEMO_MAX_LEN = 200;
export function setMoveMemo(value) {
  setSilent((s) => ({ move: { ...s.move, memo: value.slice(0, MEMO_MAX_LEN) } }));
}
export function setFromAccount(accountId) {
  const w = getState().wallets.find((w) => w.id === accountId);
  const firstCcy = Object.keys(w.balances)[0] || getState().reportingCurrency;
  setState((s) => ({
    move: { ...s.move, fromAccountId: accountId, ccy: firstCcy, toKey: '', ...RESET_DELIVER_FIELDS, errors: {} }
  }));
}

// Real crash bug, found via Playwright testing, not hypothetical:
// move.fromAccountId can be '' (store.js's own initial value) even when
// accounts already exist — it's only ever set by saveOpenAccount's very
// first account, setFromAccount, or goTransfer, and onMoveDone resets it
// back to '' after every completed send. The Send screen itself is
// reachable more than one way without going through any of those first
// (goSend from the tab bar, but also recipients.js's back button via the
// generic go('move')), so patching every navigation entry point isn't
// robust — a future new one would silently reintroduce this. send.js's
// renderForm already falls back to `|| s.wallets[0]` for exactly this
// reason; this mirrors that same, already-trusted render-layer pattern
// for the action functions below that also need a real account.
function currentSendAccount(s) {
  return s.wallets.find((w) => w.id === s.move.fromAccountId) || s.wallets[0];
}

// Clicking a balance row in Send's balance box (Home-style, one row per
// currency the From account holds) sets both the currency and amount to
// that row's full balance in one action — this replaces the removed "Max"
// button as the way to pick a whole-balance amount, but also works for
// any currency, not just the one already selected.
export function setSendBalance(ccy) {
  const s = getState();
  const account = currentSendAccount(s);
  // account.balances[ccy] is an exact decimal string (AUDIT.md F2) —
  // Number() here is the display/input-field conversion, not storage.
  const avail = ccy === s.reportingCurrency ? totalUSDFor(account) : Number(account.balances[ccy] || 0);
  setState((st) => ({
    move: { ...st.move, ccy, amount: String(+avail.toFixed(2)), ...RESET_DELIVER_FIELDS, errors: { ...st.move.errors, ccy: null, amount: null, limit: null } }
  }));
}

export function validateMove() {
  const s = getState();
  const m = s.move;
  const errors = {};
  const account = currentSendAccount(s);
  // account.balances[m.ccy] is an exact decimal string (AUDIT.md F2).
  const avail = m.ccy === s.reportingCurrency ? totalUSDFor(account) : Number(account.balances[m.ccy] || 0);
  // Currency-conversion "deliver as" (feature-swap.md): block continuing to
  // review when deliverCcy is set but the resolved settlement chain isn't
  // Ethereum — a real, reachable case now that deliver-as is never forced
  // (buildTransferInputs' comment: USDT-via-Send defaults to Tron; Transfer
  // can independently reach Tron via its own chain selector while
  // deliverCcy is still set). A real field error, not a silent no-op —
  // deliverCcy would otherwise just be silently ignored by
  // buildTransferInputs, sending a plain, non-converting transfer with no
  // explanation for why "deliver as" appeared to do nothing.
  const deductCcyForSwapCheck = m.ccy === s.reportingCurrency ? Object.keys(account.balances)[0] : m.ccy;
  if (m.deliverCcy && m.deliverCcy !== deductCcyForSwapCheck && !swapDeliverAvailable(NET[deductCcyForSwapCheck] || '')) {
    errors.deliverCcy = translate('errors.swapUnavailableForChain', { chain: NET[deductCcyForSwapCheck] || '' });
  }
  if (!isPositiveDecimalString(m.amount)) errors.amount = translate('errors.validAmount');
  else if (Number(m.amount) > avail) errors.amount = translate('errors.exceedsBalance');
  else if (Number(m.amount) === avail && avail > 0 && isGaslessCapable(deductCcyForSwapCheck, NET[deductCcyForSwapCheck] || '')) {
    // Gasless (GasFree/ERC-4337 paymaster) fees are pulled from the same
    // token balance via a separate postOp transferFrom *after* the main
    // transfer executes (see tether.worker.js's signTransfer/quoteTransfer
    // and buildTransferInputs above) -- sending the entire balance leaves
    // nothing for that pull, and the whole UserOperation reverts on-chain
    // with a raw "TransferFromFailed()"/"AA50 postOp reverted" error at
    // broadcast time. Caught live 2026-08-12: a real 20.00 USDC send
    // against a 20.00 balance reverted this way. migrateTreasury already
    // documents this exact class of bug for its own 100%-of-balance
    // sweep (deliberately non-gasless there, see its own comment); this
    // is the same structural gap in the regular Send flow, closed here
    // with a real explanation instead of surfacing the bundler's raw
    // revert reason after a live quote attempt.
    errors.amount = translate('errors.gaslessNeedsHeadroom');
  } else if (
    account && account.sendLimit != null &&
    !(m.amount === m.limitOverrideForAmount && m.ccy === m.limitOverrideForCcy && m.fromAccountId === m.limitOverrideForAccountId)
  ) {
    // feature-harden.md 6.6: enforced against the mock signTransfer call
    // site (see 6.0's deferral) — the data model/enforcement is real, but
    // there's no real broadcast underneath it yet. toReporting normalizes
    // any stablecoin to the account's own sendLimit, which is itself
    // expressed in the current reporting currency.
    const amtInReporting = toReporting(m.ccy, Number(m.amount));
    if (amtInReporting > account.sendLimit) {
      errors.limit = translate('errors.overSendLimit', { limit: fmtReporting(account.sendLimit) });
    }
  }
  if (!m.toKey) errors.toKey = translate('errors.chooseDestination');
  else {
    // feature-harden.md 6.5: chain-aware at the point a chain is actually
    // knowable — recipients themselves have no chain field (see
    // validateRecipientAddressBlur's comment), but once a real stablecoin
    // is selected (CONFIG.NET[m.ccy] resolves to an actual chain, not the
    // reporting-currency's "no network yet" case), a saved recipient whose
    // address is structurally wrong for *that* chain must block Continue,
    // not just look plausible. An internal 'account:' destination is
    // always this app's own derived address for the right chain by
    // construction, so only 'recipient:' needs the check.
    const [kind, refId] = m.toKey.split(':');
    if (kind === 'recipient') {
      const chain = CONFIG.NET[m.ccy];
      const recipient = s.savedRecipients.find((r) => r.id === refId);
      if (chain && recipient && !isValidAddressForChain(recipient.address, chain)) {
        errors.toKey = translate('errors.recipientWrongChain', { chain });
      }
      // CCSS-KU (feature-harden.md section 2 / AUDIT.md F3): new-recipient
      // cooldown + first-payment echo-test, both default-on. Only checked
      // once the recipient is otherwise valid for this send (skip if the
      // chain check above already errored) and, like errors.limit above,
      // only once the amount itself is otherwise valid — real bug, found
      // via Playwright: without the `!errors.amount` guard, the override
      // button (send.js) could render while the *displayed* error text was
      // still "Exceeds available balance" (send.js's single error slot
      // shows amount/limit/cooldown in that priority order), a mismatched
      // "why does this button exist" pairing. Only ever for recipients
      // that haven't completed a first payment yet (onMoveConfirm marks
      // firstPaymentVerified once one succeeds — see there). Grandfathered
      // via `recipient.createdAt != null`: recipients saved before this
      // policy shipped have no createdAt, and without this guard the
      // cooldown math's `(recipient.createdAt || 0)` treats a missing
      // createdAt as epoch 0, so `msRemaining` goes hugely negative and
      // falls straight through to the echo-limit branch — capping every
      // pre-existing recipient at the echo limit on every send. There's no
      // real creation time to grandfather from, so skip the policy
      // entirely for these rather than guess one (advisor-caught, see
      // feature-harden.md 6.17). Overriding re-requires a fresh re-auth if
      // toKey changes afterward, same "cleared only while it still matches
      // what was approved" pattern as the send-limit override above.
      if (!errors.toKey && !errors.amount && recipient && recipient.createdAt != null && !recipient.firstPaymentVerified && m.toKey !== m.newRecipientOverrideForToKey) {
        const msRemaining = NEW_RECIPIENT_COOLDOWN_MS - (Date.now() - (recipient.createdAt || 0));
        const amtInReporting = isPositiveDecimalString(m.amount) ? toReporting(m.ccy, Number(m.amount)) : 0;
        if (msRemaining > 0) {
          const hours = Math.max(1, Math.ceil(msRemaining / (60 * 60 * 1000)));
          errors.cooldown = translate('errors.newRecipientCooldown', { hours });
        } else if (amtInReporting > ECHO_PAYMENT_LIMIT) {
          errors.cooldown = translate('errors.newRecipientEchoLimit', { limit: fmtReporting(ECHO_PAYMENT_LIMIT) });
        }
      }
    }
  }
  return errors;
}

// feature-harden.md 6.6: exceeding a per-account send limit isn't a dead
// end — it requires the same re-auth gate as a key reveal or recipient
// change (confirmRevealPurpose), the "owner-override-with-re-auth path"
// the doc asks for. This app has exactly one authenticated party per
// profile (see 6.7's deferral — no multi-role/multi-key model exists
// yet), so "owner override" here means re-entering this profile's own
// PIN/WebAuthn, not a second person's approval; commitMoveLimitOverride
// records *which* amount/ccy/account was approved so editing any of them
// afterward silently re-requires a fresh override rather than carrying
// stale approval forward (see the move-state comment in store.js).
export function requestMoveLimitOverride() {
  setState({ showConfirmReveal: true, confirmRevealPurpose: 'moveLimitOverride', confirmRevealCode: '', confirmRevealError: '' });
}
function commitMoveLimitOverride() {
  setState((s) => ({
    move: {
      ...s.move,
      limitOverrideForAmount: s.move.amount, limitOverrideForCcy: s.move.ccy, limitOverrideForAccountId: s.move.fromAccountId,
      errors: { ...s.move.errors, limit: null }
    }
  }));
}

// CCSS-KU's new-recipient cooldown/echo-test override (AUDIT.md F3) —
// same re-auth-gated override pattern as commitMoveLimitOverride above,
// covering both reasons validateMove can set errors.cooldown (still in
// cooldown, or the first payment exceeds the echo limit) with one
// override rather than two near-duplicate flows.
export function requestNewRecipientOverride() {
  setState({ showConfirmReveal: true, confirmRevealPurpose: 'newRecipientOverride', confirmRevealCode: '', confirmRevealError: '' });
}
function commitNewRecipientOverride() {
  setState((s) => ({
    move: { ...s.move, newRecipientOverrideForToKey: s.move.toKey, errors: { ...s.move.errors, cooldown: null } }
  }));
}

// Blur-time validation only checks *format* (amount parses, isn't over
// balance) — not required-ness, which stays a submit-time check so tabbing
// through an empty field doesn't show an error before the user has typed
// anything. Real setState, not setSilent: every other field on this screen
// (the from-account/to/currency selects, all setMoveField/setFromAccount)
// already rebuilds the whole DOM via setState on change without losing a
// subsequent click, since app.js's render() only needs its reentrancy
// guard for a render triggered *during* another render's own teardown, not
// for an ordinary blur-then-click sequence. A prior version of this
// function used setSilent instead, on the mistaken assumption that any
// setState-from-blur here risked swallowing a click the way the (separate,
// already root-caused) Landing passcode / Add Recipient reentrancy bug did
// — that meant this validation computed the right error but never actually
// rendered it, so it only ever became visible once some unrelated later
// setState happened to fire (e.g. clicking "Continue to review", whose own
// validateMove() call finally showed it) — caught live via UAT, 2026-08-13.
export function validateMoveAmountBlur() {
  const s = getState();
  const m = s.move;
  if (!m.amount) {
    setState((st) => ({ move: { ...st.move, errors: { ...st.move.errors, amount: null } } }));
    return;
  }
  const account = currentSendAccount(s);
  // account.balances[m.ccy] is an exact decimal string (AUDIT.md F2).
  const avail = m.ccy === s.reportingCurrency ? totalUSDFor(account) : Number(account.balances[m.ccy] || 0);
  let error = null;
  if (!isPositiveDecimalString(m.amount)) error = translate('errors.validAmount');
  else if (Number(m.amount) > avail) error = translate('errors.exceedsBalance');
  setState((st) => ({ move: { ...st.move, errors: { ...st.move.errors, amount: error } } }));
}

// The chain's own asset, not a stablecoin — real ERC-20/TRC-20 transfers
// pay gas in this, never in the token being sent. Fee-display only; no
// other handler needs this (getBalance/getTransactions are always about a
// specific stablecoin's balance, not the chain's native asset).
export const NATIVE_CCY = { 'Ethereum · ERC-20': 'ETH', 'Tron · TRC-20': 'TRX' };

// Resolves m.toKey (an internal 'account:<id>'/'recipient:<id>' reference)
// to a real chain address — worker handlers take a real address, never an
// app-internal reference, same "caller resolves inputs" pattern as
// tokenAddress/decimals below. Recipients' addresses are already in
// (decrypted, post-unlock) state; internal accounts reuse the address
// ensureHomeAddresses() cached at boot, falling back to a fresh
// deriveAddress call on a cache miss (e.g. an account opened after boot,
// before its own ensureHomeAddresses pass has landed) rather than
// resolving to nothing.
async function resolveToAddress(s, toKey, ccy, chain, testnetEnabled) {
  const [kind, refId] = toKey.split(':');
  if (kind === 'recipient') {
    const r = s.savedRecipients.find((r) => r.id === refId);
    return r ? r.address : null;
  }
  const cached = s.homeAddresses[refId + '|' + ccy];
  if (cached) return cached;
  const { address } = await walletCall('deriveAddress', { accountId: refId, ccy, chain, testnetEnabled, gasless: isGaslessCapable(ccy, chain) });
  return address;
}

// Exact-decimal-string amount, safe for ethers' parseUnits (advisor-caught
// 2026-08-06, AUDIT.md F2's remaining half): the FX-converted path used to
// hand the worker a raw float-division Number -- e.g. 100 EUR / (a real
// ~0.92 EUR/USDC rate) = 108.69565217391305, 14 fractional digits, which
// parseUnits throws on for a 6-decimal token. That was silent dead code
// while signTransfer was a mock; it's a real bug now every FX-converted
// send would hit. The common case (no FX conversion) passes m.amount
// straight through unchanged -- already a strict decimal string
// (isPositiveDecimalString), no float round-trip, and no risk of
// toFixed's own rounding touching an amount the user typed exactly.
// TOCTOU re-quote tolerance for onMoveConfirm's swap branch below (0.5%) —
// tight enough to catch a real, meaningful price move between the review
// screen loading and Confirm being tapped, loose enough that ordinary
// sub-second stablecoin-pair jitter doesn't false-positive-block a normal
// confirm.
const SWAP_REQUOTE_TOLERANCE = 0.005;

function deductAmountString(m, deductCcy, decimals) {
  if (m.ccy === deductCcy) return m.amount;
  const fxRate = toReporting(deductCcy, 1);
  return (Number(m.amount) / fxRate).toFixed(decimals);
}

// Shared by fetchReviewFee (quote) and onMoveConfirm (real send) so the two
// can never quote a different amount/address than what actually gets
// signed. Returns null if the destination can't be resolved to a real
// address (shouldn't happen post-validateMove, but both callers treat a
// null toAddress as a hard stop rather than guessing).
async function buildTransferInputs(s) {
  const m = s.move;
  const src = currentSendAccount(s);
  const deductCcy = m.ccy === s.reportingCurrency ? Object.keys(src.balances)[0] : m.ccy;
  const chain = NET[deductCcy] || '';
  const tokenAddress = tokenAddressFor(deductCcy, chain);
  const decimals = CONFIG.TOKEN_DECIMALS[deductCcy];
  const amount = deductAmountString(m, deductCcy, decimals);
  const toAddress = await resolveToAddress(s, m.toKey, deductCcy, chain, s.testnetEnabled);
  if (!toAddress) return null;
  const gasless = isGaslessCapable(deductCcy, chain);
  const base = { fromAccountId: src.id, chain, toAddress, tokenAddress, amount, decimals, testnetEnabled: s.testnetEnabled, gasless, deductCcy };
  // Currency-conversion "deliver as" (feature-swap.md) — only ever active
  // when deliverCcy is a real, different currency AND the *already-
  // resolved* settlement chain is Ethereum (swapDeliverAvailable). Never
  // forced: this app's wallets[].balances is keyed per-currency only (one
  // balance per ccy, always fetched against NET[ccy]'s default chain, see
  // ensureAccountBalances) — there is no per-chain balance model, so
  // forcing e.g. a USDT-on-Tron send onto Ethereum would validate and quote
  // against a balance the swap wouldn't actually be spending from. USDT
  // via Send is the one real case this excludes (CNHT already resolves to
  // Ethereum via NET); deliberately deferred rather than shipped unsafely,
  // logged in feature-outstanding.md.
  if (!m.deliverCcy || m.deliverCcy === deductCcy || !swapDeliverAvailable(chain)) return base;
  const tokenOutAddress = tokenAddressFor(m.deliverCcy, chain);
  const decimalsOut = CONFIG.TOKEN_DECIMALS[m.deliverCcy];
  return { ...base, swapping: true, tokenOutAddress, decimalsOut };
}

// Real on-chain fee quote for the review/signing-confirmation screen
// (CLAUDE.md: fee must be visible before signing) — replaces the old
// hardcoded amount*0.001 heuristic (feature-backend.md item 8). Never
// awaited from onMoveContinue (same "don't block a nav transition on a
// network call" precedent as ensureAccountBalances) — the review screen
// renders immediately with reviewFeeLoading true, then this patches in the
// real quote (or a Tron/resolution error) once it resolves. Guards against
// a stale response landing after the user has already left review (Back,
// Confirm, or Done) by checking the step is still 'review' before setting
// state.
async function fetchReviewFee() {
  const s = getState();
  setState((st) => ({
    move: { ...st.move, reviewFeeLoading: true, reviewFeeError: null, reviewSwapLoading: true, reviewSwapError: null }
  }));
  let swapping = false;
  try {
    const inputs = await buildTransferInputs(s);
    if (!inputs) throw new Error('Could not resolve destination address');
    swapping = !!inputs.swapping;
    if (swapping) {
      // Currency-conversion quote (feature-swap.md Phase 2's
      // quoteSwapDeliver) — its own shape (needsApproval/approveFee/fee/
      // tokenOutAmount), not quoteTransfer's, so mapped into the dedicated
      // reviewSwap* fields rather than the plain reviewFee ones.
      const result = await walletCall('quoteSwapDeliver', inputs);
      if (getState().move.step !== 'review') return;
      setState((st) => ({
        move: {
          ...st.move, reviewFeeLoading: false, reviewSwapLoading: false,
          reviewSwapNeedsApproval: result.needsApproval,
          reviewSwapApproveFee: result.approveFee,
          reviewSwapApproveFeeAsset: result.approveFee != null ? result.feeAsset : null,
          reviewSwapFee: result.fee,
          reviewSwapFeeAsset: result.fee != null ? result.feeAsset : null,
          reviewSwapOut: result.tokenOutAmount
        }
      }));
      return;
    }
    const { fee, feeAsset } = await walletCall('quoteTransfer', inputs);
    if (getState().move.step !== 'review') return;
    // Gasless only: the fee comes out of the same token balance as the
    // amount being sent (GasFree/Pimlico both charge in-token — see
    // tether.worker.js's quoteTransfer comment), so amount+fee can exceed
    // the real balance in a way a native-gas fee never could (that always
    // came out of a *different* balance). Caught here, before Confirm is
    // even enabled, rather than surfacing as an opaque broadcast failure.
    if (inputs.gasless) {
      const src = s.wallets.find((w) => w.id === inputs.fromAccountId);
      const balance = Number((src && src.balances[inputs.deductCcy]) || 0);
      if (Number(inputs.amount) + Number(fee) > balance) {
        setState((st) => ({
          move: { ...st.move, reviewFeeLoading: false, reviewSwapLoading: false, reviewFeeError: translate('send.reviewFeeInsufficientForFee', { fee, ccy: inputs.deductCcy }) }
        }));
        return;
      }
    }
    setState((st) => ({ move: { ...st.move, reviewFee: fee, reviewFeeAsset: feeAsset || null, reviewFeeLoading: false, reviewSwapLoading: false } }));
  } catch (err) {
    if (getState().move.step !== 'review') return;
    if (swapping) {
      setState((st) => ({
        move: { ...st.move, reviewFeeLoading: false, reviewSwapLoading: false, reviewSwapError: err.message || 'Quote unavailable' }
      }));
      return;
    }
    setState((st) => ({
      move: { ...st.move, reviewFeeLoading: false, reviewSwapLoading: false, reviewFeeError: err.message || 'Fee unavailable' }
    }));
  }
}

export function onMoveContinue() {
  const errors = validateMove();
  if (Object.keys(errors).length) { setState((s) => ({ move: { ...s.move, errors } })); return; }
  setState((s) => ({
    move: {
      ...s.move, step: 'review', reviewFee: null, reviewFeeAsset: null, reviewFeeError: null, mainnetAck: false,
      reviewSwapOut: null, reviewSwapError: null, reviewSwapNeedsApproval: false,
      reviewSwapApproveFee: null, reviewSwapApproveFeeAsset: null, reviewSwapFee: null, reviewSwapFeeAsset: null
    }
  }));
  fetchReviewFee();
}
export function onMoveBack() {
  setState((s) => ({ move: { ...s.move, step: 'form' } }));
}

export async function onMoveConfirm() {
  // Guard against double-submit: a fast double-click before the first
  // re-render lands must not fire two signed transfers.
  if (getState().move.sending) return;
  setState((s) => ({ move: { ...s.move, sending: true, errors: { ...s.move.errors, submit: null } } }));

  const s = getState();
  const m = s.move;
  // m.amount is a validated (isPositiveDecimalString) decimal string by
  // this point — Number() on an already-format-checked string is exact,
  // unlike parseFloat which would silently accept trailing garbage. Kept
  // only for the success screen's display (resultTx.amount, in the
  // originally-entered ccy) — the actual signed amount comes from
  // buildTransferInputs below, in deductCcy's units.
  const amt = Number(m.amount);

  let txId, hash, hashPending, chain, tokenOutAmount, approveTxId;
  try {
    const inputs = await buildTransferInputs(s);
    if (!inputs) throw new Error('Could not resolve destination address');
    chain = inputs.chain;
    if (inputs.swapping) {
      // TOCTOU re-quote guard (feature-swap.md Phase 0/4, DECISIONS.md
      // 2026-08-10 correction): quoteSwap() (shown on the review screen)
      // and swap() (fired here) each independently re-quote — swap()'s own
      // on-chain floor only protects *its own* internal re-quote-to-
      // broadcast window, not the time the user actually spent looking at
      // the review screen. Without this, a price move during that dwell
      // time would let swap() silently re-quote and execute at a worse
      // rate than what was shown/confirmed — no revert, no error. Re-quote
      // immediately before signing and refuse to proceed if the output has
      // drifted beyond a fixed tolerance from what the user actually saw.
      const fresh = await walletCall('quoteSwapDeliver', inputs);
      const shown = Number(m.reviewSwapOut);
      const freshOut = Number(fresh.tokenOutAmount);
      if (!Number.isFinite(shown) || !Number.isFinite(freshOut) || Math.abs(freshOut - shown) / shown > SWAP_REQUOTE_TOLERANCE) {
        throw new Error(translate('send.swapQuoteStale'));
      }
      ({ txId, hash, tokenOutAmount, approveTxId } = await walletCall('signSwapDeliver', inputs));
    } else {
      ({ txId, hash, hashPending } = await walletCall('signTransfer', inputs));
    }
  } catch (err) {
    const detail = err && err.message ? ` (${err.message})` : '';
    setState((st) => ({ move: { ...st.move, sending: false, errors: { ...st.move.errors, submit: `Transfer failed — try again${detail}` } } }));
    return;
  }

  // resultLabel is still resolved locally — needed for the success
  // screen's copy regardless of transaction-history source.
  const [kind, refId] = m.toKey.split(':');
  let resultLabel = '';
  if (kind === 'account') {
    const dst = s.wallets.find((w) => w.id === refId);
    resultLabel = dst.name;
  } else {
    const recipient = s.savedRecipients.find((r) => r.id === refId) || { name: 'Unknown' };
    resultLabel = recipient.name;
  }
  // CCSS-KU (AUDIT.md F3): a completed first payment to a recipient --
  // whether it cleared the echo-test/cooldown check naturally or needed
  // an explicit re-auth override -- means future payments to them no
  // longer need either. Only ever flips false -> true, never reset here
  // (an address change resets it separately, see commitSaveRecipient).
  if (kind === 'recipient') {
    const recipientList = s.savedRecipients.map((r) => (r.id === refId ? { ...r, firstPaymentVerified: true } : r));
    setState({ savedRecipients: recipientList });
    persistRecipients(recipientList);
  }
  haptic(25);
  setState((st) => ({
    move: {
      ...st.move, step: 'success', sending: false,
      resultTx: {
        id: txId, hash, hashPending: !!hashPending, chain, amount: amt, ccy: m.ccy, label: resultLabel,
        deliverCcy: m.deliverCcy || null, tokenOutAmount: tokenOutAmount || null, approveTxId: approveTxId || null
      }
    }
  }));
  // Real balance/history refresh, not a local fabrication (CLAUDE.md/
  // feature-backend.md items 5-6) — signTransfer is now a real broadcast
  // (2026-08-06, EVM only) too, so this refresh will show the real
  // post-send balance once the RPC/indexer catch up, not immediately (a
  // freshly broadcast tx isn't always reflected on the very next read).
  // Neither call is awaited — the success screen must show immediately,
  // not wait on a background refresh (see loadWalletForSession's comment
  // on why this is never awaited from a critical path).
  ensureAccountBalances();
  ensureRecentTransactions();
}

export function onMoveDone() {
  setState({
    move: {
      fromAccountId: '', ccy: 'USDC', chain: NET.USDC, toKey: '', amount: '', memo: '', step: 'form', errors: {}, resultTx: null, sending: false,
      reviewFee: null, reviewFeeAsset: null, reviewFeeLoading: false, reviewFeeError: null, mainnetAck: false,
      limitOverrideForAmount: null, limitOverrideForCcy: null, limitOverrideForAccountId: null,
      newRecipientOverrideForToKey: null
    },
    screen: 'home'
  });
}

/* ---- Migrate treasury (RECOVERY.md / feature-outstanding.md, 2026-08-07) */
// One-click auto-sweep of every non-zero stablecoin balance on this
// profile to a user-supplied destination address, replacing RECOVERY.md's
// documented manual 4-step workaround. Deliberately does NOT touch
// savedRecipients (RECOVERY.md's own step 3: "re-create your allowlist
// from scratch... a compromised device's saved recipients are not more
// trustworthy than a manually re-typed, re-verified list" — this flow
// only moves funds, never migrates trust).
//
// Chains here are a UI-model constant, not a network call — mirrors
// send.js/transfer.js's own reliance on CONFIG.NET being fixed.
const MIGRATE_CHAINS = ['Ethereum · ERC-20', 'Tron · TRC-20'];

export function openMigrateTreasury() {
  setState({
    showMigrateTreasury: true,
    migrateTreasury: {
      step: 'form',
      toAddress: { 'Ethereum · ERC-20': '', 'Tron · TRC-20': '' },
      errors: {},
      legs: [],
      mainnetAck: false,
      sending: false
    }
  });
}
export function cancelMigrateTreasury() {
  setState({ showMigrateTreasury: false });
}
export function setMigrateTreasuryMainnetAck(checked) {
  setState((s) => ({ migrateTreasury: { ...s.migrateTreasury, mainnetAck: checked } }));
}
export function setMigrateTreasuryAddress(chain, value) {
  setSilent((s) => ({
    migrateTreasury: {
      ...s.migrateTreasury,
      toAddress: { ...s.migrateTreasury.toAddress, [chain]: value },
      errors: { ...s.migrateTreasury.errors, [chain]: null }
    }
  }));
}

// Every (accountId, ccy) with a real positive balance, across every
// account on this profile — same "every account, not just the current
// Send account" scope RECOVERY.md's manual workaround describes ("move
// balances to the new profile's addresses", plural, implicitly all of
// them). isPositiveDecimalString rather than `> 0` on the raw string:
// balances are decimal strings from the worker's own formatUnits, not
// numbers, same as everywhere else in this file.
function nonZeroLegSources(s) {
  const legs = [];
  s.wallets.forEach((w) => {
    Object.entries(w.balances).forEach(([ccy, amount]) => {
      if (isPositiveDecimalString(amount)) {
        legs.push({ accountId: w.id, accountName: w.name, ccy, chain: NET[ccy] || '' });
      }
    });
  });
  return legs;
}

// Which destination-address fields the form actually needs to show/
// validate — only the chains at least one real leg would settle on, not
// every chain this app knows about (a profile with only Ethereum balances
// shouldn't be blocked on entering a Tron address it'll never use).
export function migrateTreasuryChainsNeeded(s) {
  const sources = nonZeroLegSources(s);
  return MIGRATE_CHAINS.filter((chain) => sources.some((l) => l.chain === chain));
}

export function onMigrateTreasuryContinue() {
  const s = getState();
  const chainsNeeded = migrateTreasuryChainsNeeded(s);
  const errors = {};
  chainsNeeded.forEach((chain) => {
    const addr = (s.migrateTreasury.toAddress[chain] || '').trim();
    if (!isValidAddressForChain(addr, chain)) errors[chain] = translate('modals.migrateTreasury.invalidAddress');
  });
  if (Object.keys(errors).length) {
    setState((st) => ({ migrateTreasury: { ...st.migrateTreasury, errors } }));
    return;
  }

  // Deliberately NOT gasless (feature-outstanding.md, 2026-08-07): every
  // leg here sweeps `wallet.balances[src.ccy]` — the *entire* balance — and
  // a gasless fee comes out of that same token, not native currency (see
  // buildTransferInputs' analogous check in the Send/Transfer flow). A
  // 100%-of-balance leg would leave no room for its own fee and fail with
  // an opaque error (or worse, partially strand funds mid-sweep). Fixing
  // that correctly needs gasCheckFor (below) to understand per-token
  // headroom, not just native-currency headroom, plus reserving the fee
  // out of a 100% sweep — real work, not a flag flip, and not worth
  // rushing on a flow that moves an entire treasury in one pass. Every leg
  // here still uses real native-gas quoteTransfer/signTransfer, unchanged.
  const legs = nonZeroLegSources(s).map((src) => {
    const tokenAddress = tokenAddressFor(src.ccy, src.chain);
    const decimals = CONFIG.TOKEN_DECIMALS[src.ccy];
    const wallet = s.wallets.find((w) => w.id === src.accountId);
    return {
      ...src,
      amount: wallet.balances[src.ccy],
      tokenAddress, decimals,
      toAddress: s.migrateTreasury.toAddress[src.chain].trim(),
      fee: null, feeLoading: true, feeError: null, nativeBalance: null,
      status: 'pending', txHash: null, errorMsg: null
    };
  });
  setState((st) => ({ migrateTreasury: { ...st.migrateTreasury, step: 'review', legs, mainnetAck: false } }));
  legs.forEach((_, i) => fetchMigrateTreasuryLegQuote(i));
}

export function onMigrateTreasuryBack() {
  setState((s) => ({ migrateTreasury: { ...s.migrateTreasury, step: 'form' } }));
}

// Never awaited from onMigrateTreasuryContinue — same "don't block a nav
// transition on a network call" precedent as fetchReviewFee/
// ensureAccountBalances. Guards against a stale response landing after the
// user has already left the review step, same pattern as fetchReviewFee.
// Deliberately stores only the raw fee/nativeBalance here, not a derived
// "is there enough gas" verdict — see gasCheckFor below for why that has
// to be computed fresh from the whole legs array, not baked in per-leg at
// fetch time.
async function fetchMigrateTreasuryLegQuote(index) {
  const s = getState();
  const leg = s.migrateTreasury.legs[index];
  if (!leg) return;
  const testnetEnabled = s.testnetEnabled;
  try {
    const [{ fee }, { balance: nativeBalance }] = await Promise.all([
      walletCall('quoteTransfer', {
        fromAccountId: leg.accountId, chain: leg.chain, toAddress: leg.toAddress,
        tokenAddress: leg.tokenAddress, amount: leg.amount, decimals: leg.decimals, testnetEnabled
      }),
      walletCall('getNativeBalance', { accountId: leg.accountId, chain: leg.chain, testnetEnabled })
    ]);
    if (getState().migrateTreasury.step !== 'review') return;
    setState((st) => ({
      migrateTreasury: {
        ...st.migrateTreasury,
        legs: st.migrateTreasury.legs.map((l, i) => (i === index ? { ...l, fee, feeLoading: false, nativeBalance } : l))
      }
    }));
  } catch (err) {
    if (getState().migrateTreasury.step !== 'review') return;
    setState((st) => ({
      migrateTreasury: {
        ...st.migrateTreasury,
        legs: st.migrateTreasury.legs.map((l, i) => (i === index ? { ...l, feeLoading: false, feeError: err.message || 'Fee unavailable' } : l))
      }
    }));
  }
}

// Whether a leg's (accountId, chain) group has enough native currency for
// every leg in that group whose fee is already known — computed fresh from
// the current legs array every time, not persisted per-leg state. Multiple
// legs can share one account+chain (e.g. sweeping both USDC and CNHT from
// the same Ethereum account), and each fetches its own quote independently
// (Promise.all per leg, not sequentially), so baking a "nativeOk" verdict
// into leg state at fetch time would race: an earlier-resolving leg could
// compute its verdict before a sibling's fee is known yet, and never get
// recomputed once the sibling's fee lands. Recomputing here on every
// render instead means it's always consistent with the latest known data.
// Returns null (unknown — still loading) while any sibling's quote hasn't
// resolved yet, so the review screen can show "checking…" honestly rather
// than a premature ok/not-ok.
export function gasCheckFor(legs, leg) {
  const siblings = legs.filter((l) => l.accountId === leg.accountId && l.chain === leg.chain);
  if (siblings.some((l) => l.feeLoading)) return null;
  const totalFee = siblings.reduce((sum, l) => sum + (l.fee != null ? Number(l.fee) : 0), 0);
  const nativeBalance = siblings.find((l) => l.nativeBalance != null);
  if (!nativeBalance) return null;
  return Number(nativeBalance.nativeBalance) >= totalFee;
}

// A leg is actually executable once its own quote has resolved
// successfully and its (accountId, chain) group has enough native
// currency for every leg sharing it — a quote error (Tron, an unreachable
// RPC, whatever) or an insufficient-gas group is surfaced on the review
// screen but skipped at execution time, never silently retried or
// blocked-on. Aborting the entire sweep over one bad leg would be worse
// than sweeping what's actually possible and reporting the rest — see
// onMigrateTreasuryConfirm below and DECISIONS.md.
function isLegExecutable(legs, leg) {
  return !leg.feeLoading && !leg.feeError && leg.fee != null && gasCheckFor(legs, leg) === true;
}

export function migrateTreasuryReviewReady(migrateTreasury) {
  return migrateTreasury.legs.every((l) => !l.feeLoading);
}

// CLAUDE.md: "every signing action requires an explicit, human-readable
// confirmation screen... never auto-sign" — satisfied here the same way a
// single Send/Transfer confirm is: every leg's account, currency, amount,
// destination, and real fee is shown on the review screen *before* this
// fires, and this itself is a single explicit tap, not something that
// fires automatically. What it triggers is different from Send/Transfer
// (N real broadcasts instead of one), which is exactly why every leg's
// detail must already be visible above it — nothing here is signed that
// the user didn't see first.
export async function onMigrateTreasuryConfirm() {
  if (getState().migrateTreasury.sending) return;
  setState((s) => ({ migrateTreasury: { ...s.migrateTreasury, sending: true } }));

  const testnetEnabled = getState().testnetEnabled;
  const legCount = getState().migrateTreasury.legs.length;
  for (let i = 0; i < legCount; i++) {
    const legs = getState().migrateTreasury.legs;
    const leg = legs[i];
    if (!isLegExecutable(legs, leg)) {
      setState((st) => ({
        migrateTreasury: { ...st.migrateTreasury, legs: st.migrateTreasury.legs.map((l, idx) => (idx === i ? { ...l, status: 'skipped' } : l)) }
      }));
      continue;
    }
    setState((st) => ({
      migrateTreasury: { ...st.migrateTreasury, legs: st.migrateTreasury.legs.map((l, idx) => (idx === i ? { ...l, status: 'sending' } : l)) }
    }));
    try {
      // Deliberately sequential (awaited in the loop), not fired
      // concurrently: legs on the same account/chain share one native
      // balance for gas, so running them in parallel risks two legs both
      // passing the review screen's gas check against a balance only one
      // of them can actually spend.
      const { hash } = await walletCall('signTransfer', {
        fromAccountId: leg.accountId, chain: leg.chain, toAddress: leg.toAddress,
        tokenAddress: leg.tokenAddress, amount: leg.amount, decimals: leg.decimals, testnetEnabled
      });
      setState((st) => ({
        migrateTreasury: { ...st.migrateTreasury, legs: st.migrateTreasury.legs.map((l, idx) => (idx === i ? { ...l, status: 'done', txHash: hash } : l)) }
      }));
    } catch (err) {
      // Never abort the run — a Tron leg failing (or any other single
      // failure) must not stop legs after it from being attempted.
      setState((st) => ({
        migrateTreasury: {
          ...st.migrateTreasury,
          legs: st.migrateTreasury.legs.map((l, idx) => (idx === i ? { ...l, status: 'failed', errorMsg: err.message || 'Transfer failed' } : l))
        }
      }));
    }
  }
  setState((s) => ({ migrateTreasury: { ...s.migrateTreasury, sending: false } }));
  haptic(25);
  ensureAccountBalances();
  ensureRecentTransactions();
}

export function closeMigrateTreasury() {
  setState({ showMigrateTreasury: false });
}

/* ---- Add recipient --------------------------------------------------- */
export function openAddRecipient() {
  setState({ showAddRecipient: true, newRecipName: '', newRecipAddress: '', newRecipError: '', recipientEdit: null });
}
export function cancelAddRecipient() {
  setState({ showAddRecipient: false, recipientEdit: null });
}
// feature-harden.md 6.5: the recipient form has no chain selector of its
// own (recipients aren't chain-scoped in this app's data model — the same
// saved recipient can be picked as a destination for any stablecoin/chain
// in Send), so "valid" here means real EIP-55/base58check-valid for *some*
// supported chain, not a bare length check. The doc's fuller ask —
// rejecting an address that's wrong for the *currently selected* chain —
// is enforced downstream in validateMove below, at the point a chain
// actually is known (Send's ccy selection). See DECISIONS.md.
function isValidAnyChainAddress(address) {
  return isValidEvmAddress(address) || isValidTronAddress(address);
}
export function validateRecipientAddressBlur() {
  const s = getState();
  const addr = (s.recipientEdit ? s.recipientEdit.address : s.newRecipAddress).trim();
  if (!addr) { setState({ newRecipError: '' }); return; }
  setState({ newRecipError: isValidAnyChainAddress(addr) ? '' : translate('errors.validWalletAddress') });
}
// Handles both create (recipientEdit === null) and edit (openEditRecipient
// having populated it) — same modal, same validation, so edits get the same
// address-format check a new recipient does. Validation stays synchronous/
// immediate here (feature-harden.md 6.3 only asks to gate the actual
// allowlist *write* behind re-auth, not the format check) — only a
// passing validation opens the confirmReveal modal; commitSaveRecipient
// does the actual write once re-auth succeeds.
export function saveRecipient() {
  const s = getState();
  const editing = s.recipientEdit;
  const name = (editing ? editing.name : s.newRecipName).trim();
  const address = (editing ? editing.address : s.newRecipAddress).trim();
  if (!name) { setState({ newRecipError: translate('errors.enterName') }); return; }
  if (!isValidAnyChainAddress(address)) { setState({ newRecipError: translate('errors.validWalletAddress') }); return; }
  setState({ showConfirmReveal: true, confirmRevealPurpose: 'saveRecipient', confirmRevealCode: '', confirmRevealError: '' });
}
function commitSaveRecipient() {
  const s = getState();
  const editing = s.recipientEdit;
  const name = (editing ? editing.name : s.newRecipName).trim();
  const address = (editing ? editing.address : s.newRecipAddress).trim();

  if (editing) {
    const list = s.savedRecipients.map((r) => {
      if (r.id !== editing.id) return r;
      // Changing an existing recipient's address is functionally the
      // same risk a brand-new recipient is (CCSS-KU/AUDIT.md F3): an
      // attacker who got into the account could otherwise repoint an
      // already-"verified" trusted recipient at their own address and
      // skip the cooldown/echo-test protection entirely. Only an address
      // change resets it — renaming a recipient doesn't.
      const addressChanged = address !== r.address;
      return addressChanged
        ? { ...r, name, address, createdAt: Date.now(), firstPaymentVerified: false }
        : { ...r, name, address };
    });
    setState({ savedRecipients: list, showAddRecipient: false, recipientEdit: null });
    persistRecipients(list);
    showToast(translate('toast.recipientUpdated'));
    return;
  }

  const id = 'r' + Date.now();
  // createdAt/firstPaymentVerified back the new-recipient cooldown/echo-
  // test policy (validateMove, below) — every recipient goes through it,
  // not an opt-in.
  const rec = { id, name, address, createdAt: Date.now(), firstPaymentVerified: false };
  const list = [...s.savedRecipients, rec];
  setState((st) => ({
    savedRecipients: list,
    move: { ...st.move, toKey: 'recipient:' + id, errors: { ...st.move.errors, toKey: null } },
    showAddRecipient: false
  }));
  persistRecipients(list);
}

/* ---- Activity / txns -------------------------------------------------- */
export function setTxnFilter(f) { setState({ txnFilter: f }); }
export function setTxnAccountFilter(f) { setState({ txnAccountFilter: f }); }
export function setTxnTimeFilter(f) { setState({ txnTimeFilter: f }); }
export function setTxnSort(f) { setState({ txnSort: f }); }
// setState, not setSilent: the search term drives which rows are visible,
// so every keystroke must re-render. The input opts into app.js's
// focus/caret-preservation (data-preserve-focus) so that full re-render
// doesn't lose focus or dismiss the on-screen keyboard the way it would
// without it — see the comment on render() in app.js.
export function setTxnSearch(v) { setState({ txnSearch: v }); }
export function toggleExpand(id) {
  setState((s) => ({ expandedTxId: s.expandedTxId === id ? null : id, tagDraft: '' }));
}

function persistTxnTags(tags) {
  try { localStorage.setItem('tynti_txn_tags', JSON.stringify(tags)); } catch { /* storage unavailable */ }
}
export function setTagDraft(v) {
  setSilent({ tagDraft: v });
}
export function addTxnTag(txId) {
  const s = getState();
  const tag = s.tagDraft.trim();
  if (!tag) return;
  const existing = s.txnTags[txId] || [];
  if (existing.some((t) => t.toLowerCase() === tag.toLowerCase())) {
    setState({ tagDraft: '' });
    return;
  }
  const txnTags = { ...s.txnTags, [txId]: [...existing, tag] };
  persistTxnTags(txnTags);
  setState({ txnTags, tagDraft: '' });
}
export function removeTxnTag(txId, tag) {
  const s = getState();
  const txnTags = { ...s.txnTags, [txId]: (s.txnTags[txId] || []).filter((t) => t !== tag) };
  persistTxnTags(txnTags);
  setState({ txnTags });
}

// Both exports pull from filteredTransactions, not the raw transaction
// list — feature-ui.md asks for "the displayed activity rows", so what
// downloads must always match whatever filters/search/sort are currently
// applied on screen, not the account's full history.
// CSV is plain UTF-8 text — safe to localize its column headers. The PDF
// below deliberately isn't (see its own comment): its Standard-14 fonts
// can only render Latin-1/WinAnsi glyphs (lib/pdf.js's ascii() sanitizer
// already replaces anything else with '?'), so translating its headers
// would just produce "?????" for non-Latin locales instead of readable
// text — English throughout reads better than that in every language.
export function exportCSV() {
  const rows = [[
    translate('csv.colDate'), translate('csv.colType'), translate('csv.colDescription'),
    translate('csv.colAccount'), translate('csv.colCurrency'), translate('csv.colAmount'), translate('csv.colHash')
  ]];
  filteredTransactions(getState()).forEach((t) => rows.push([t.date, t.type, t.desc, t.wallet, t.ccy, t.amount, t.hash]));
  const csv = rows.map((r) => r.map((v) => '"' + String(v).replace(/"/g, '""') + '"').join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'tynti-transactions.csv';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  showToast(translate('toast.csvDownloaded'));
}
export function exportPDF() {
  const rows = filteredTransactions(getState()).map((t) => ({
    date: t.date,
    desc: t.desc,
    wallet: t.wallet,
    // t.amount is an exact decimal string (AUDIT.md F2) — Number() here
    // is display formatting for the PDF cell, not storage.
    amount: (Number(t.amount) < 0 ? '-' : '+') + Math.abs(Number(t.amount)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ' + t.ccy,
    hash: t.hash
  }));
  const blob = buildStatementPdf({
    subtitle: 'Account Statement',
    generatedLine: 'Generated ' + new Date().toLocaleString('en-US'),
    rows
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'tynti-statement.pdf';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  showToast(translate('toast.pdfDownloaded'));
}

/* ---- Settings: preferences ---------------------------------------------- */
const SETTINGS_KEYS = ['testnetEnabled', 'defaultStablecoin', 'defaultChain', 'autoLockMinutes'];
function persistSettings(s) {
  const patch = {};
  SETTINGS_KEYS.forEach((k) => { patch[k] = s[k]; });
  try { localStorage.setItem('tynti_settings', JSON.stringify(patch)); } catch { /* storage unavailable */ }
}
export function setTestnetEnabled(v) {
  setState({ testnetEnabled: v });
  persistSettings(getState());
  // Item 11: the network switch is not just an inert UI value — every
  // network-touching worker call now reads it (see tether.worker.js), so
  // switching leaves stale testnet-or-mainnet data on screen unless it's
  // explicitly refreshed. Same "don't leave stale state visible" lesson as
  // item 10's stale-receive-address bug (see DECISIONS.md). Fire-and-
  // forget, same reasoning as every other ensure*/checkChainConnectivity
  // call site — this must never block the toggle itself.
  ensureAccountBalances();
  ensureRecentTransactions();
  checkChainConnectivity();
}

// Explicit, visually distinct confirmation step for *enabling* mainnet
// (CLAUDE.md — see store.js's showMainnetConfirm comment for the scoping
// rationale). Turning testnet back ON stays a direct setTestnetEnabled(true)
// call from settings.js — only the risky direction routes through this.
export function requestMainnetSwitch() {
  setState({ showMainnetConfirm: true });
}
export function cancelMainnetSwitch() {
  setState({ showMainnetConfirm: false });
}
export function confirmMainnetSwitch() {
  setState({ showMainnetConfirm: false });
  setTestnetEnabled(false);
}
export function setDefaultStablecoin(ccy) {
  setState({ defaultStablecoin: ccy, defaultChain: (CHAINS[ccy] || [])[0] || '' });
  persistSettings(getState());
}
export function setDefaultChain(chain) {
  setState({ defaultChain: chain });
  persistSettings(getState());
}

// feature-harden.md 6.2: Settings' auto-lock timeout control. Options are
// capped to a small allowlist (main.js's select renders exactly these) so a
// corrupt/tampered localStorage value can't silently produce a multi-hour
// or negative timeout — autoLockMsFor below is the last line of defense for
// that on the read side.
// Frozen — this bounds what a corrupted/tampered localStorage value (or a
// caller mutating the exported array) can do; setAutoLockMinutes/
// autoLockMsFor both trust .includes() on it as their entire allowlist
// check.
export const AUTO_LOCK_MINUTE_OPTIONS = Object.freeze([1, 5, 15, 30]);
export function setAutoLockMinutes(minutes) {
  const v = AUTO_LOCK_MINUTE_OPTIONS.includes(minutes) ? minutes : 5;
  setState({ autoLockMinutes: v });
  persistSettings(getState());
}
// Pure — kept separate from main.js's timer wiring (which has no DOM test
// environment, see CLAUDE.md's Testing section) so the one part of this
// that's actual logic, not just addEventListener/setTimeout plumbing, is
// unit-tested. Falls back to the 5-minute default for anything outside the
// allowlist (unset, corrupted storage, a future stale build's stray value).
export function autoLockMsFor(minutes) {
  const v = AUTO_LOCK_MINUTE_OPTIONS.includes(minutes) ? minutes : 5;
  return v * 60 * 1000;
}

/* ---- Settings: profile details ------------------------------------------ */
export function startProfileEdit(profileId, field) {
  const p = getState().profiles.find((p) => p.id === profileId);
  if (!p) return;
  setState({ profileEdit: { profileId, field, draft: field === 'name' ? p.name : (p.email || ''), error: '' } });
}
export function setProfileEditDraft(v) {
  setSilent((s) => ({ profileEdit: { ...s.profileEdit, draft: v, error: '' } }));
}
export function cancelProfileEdit() {
  setState({ profileEdit: { profileId: null, field: null, draft: '', error: '' } });
}
export function confirmProfileEdit() {
  const s = getState();
  const { profileId, field, draft } = s.profileEdit;
  if (!profileId || !field) return;
  const value = draft.trim();
  if (field === 'email' && value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
    setState((st) => ({ profileEdit: { ...st.profileEdit, error: translate('errors.validEmail') } }));
    return;
  }
  if (field === 'name' && !value) {
    setState((st) => ({ profileEdit: { ...st.profileEdit, error: translate('errors.nameEmpty') } }));
    return;
  }
  const profiles = s.profiles.map((p) => (p.id === profileId ? { ...p, [field]: value } : p));
  persistProfiles(profiles);
  setState({ profiles, profileEdit: { profileId: null, field: null, draft: '', error: '' } });
}
export function setProfileAvatar(profileId, avatar) {
  const profiles = getState().profiles.map((p) => (p.id === profileId ? { ...p, avatar } : p));
  persistProfiles(profiles);
  setState({ profiles });
}

/* ---- Settings / private keys ------------------------------------------ */
export async function refreshKeys() {
  let keys;
  try {
    keys = await walletCall('listKeys');
  } catch {
    showToast(translate('toast.couldntLoadKeys'));
    return;
  }
  setState((s) => ({
    privateKeys: keys.map((k) => {
      const existing = s.privateKeys.find((pk) => pk.id === k.id);
      return {
        id: k.id, label: k.label,
        revealed: existing ? existing.revealed : false, words: existing ? existing.words : null,
        backupVerified: existing ? !!existing.backupVerified : false,
        backupPromptDismissed: existing ? !!existing.backupPromptDismissed : false
      };
    })
  }));
}

// Called once at boot (main.js) — the check itself is async, so this
// can't just be computed inline in store.js's initial state. Device
// capability only; whether a given profile also has a passkey enrolled is
// `profile.vault.webauthn`, checked separately in the render layer.
export async function checkWebauthnAvailable() {
  const available = await platformAuthenticatorAvailable();
  setState({ webauthnAvailable: available });
}

// Called once at boot (main.js), before any profile has unlocked. Touches
// no key material at all (see tether.worker.js's ping handler) — real key
// loading only ever happens post-unlock, in loadWalletForSession below,
// once the vault key needed to decrypt a persisted seed exists.
export async function checkWorkerConnectivity() {
  try {
    await walletCall('ping');
    setState({ workerConnected: true });
  } catch {
    setState({ workerConnected: false });
  }
}

// Called once right after a successful unlock/onboarding (unlock,
// unlockWithWebauthn, submitOpenAccount) — mirrors loadRecipientsForSession
// above, but for the profile's actual wallet seed rather than its saved
// recipients. A profile's seed is generated exactly once (its first ever
// unlock after this feature existed) and persisted encrypted from then on;
// every unlock after that loads the same seed back rather than minting a
// new one — the bug this whole flow exists to avoid (see DECISIONS.md: a
// seed that's regenerated on every reload is fund loss, not a UX nit).
async function loadWalletForSession() {
  const { profileId, vaultKey } = getSessionVault();
  if (!vaultKey) return;
  const profile = getState().profiles.find((p) => p.id === profileId);
  if (!profile) return;

  try {
    const raw = profile.walletKeyId ? localStorage.getItem('tynti_seed_enc_' + profileId) : null;
    if (profile.walletKeyId && raw) {
      await walletCall('loadSeedFromStorage', { id: profile.walletKeyId, vaultKey, envelope: JSON.parse(raw) });
    } else {
      const created = await walletCall('newKey', { label: 'Default' });
      const envelope = await walletCall('encryptSeedForStorage', { id: created.id, vaultKey });
      try { localStorage.setItem('tynti_seed_enc_' + profileId, JSON.stringify(envelope)); } catch { /* storage unavailable */ }
      const profiles = getState().profiles.map((p) => (p.id === profileId ? { ...p, walletKeyId: created.id } : p));
      persistProfiles(profiles);
      setState({ profiles });
    }
    setState({ workerConnected: true });
  } catch {
    setState({ workerConnected: false });
    return;
  }
  await refreshKeys();
  await loadWalletsForSession();
  await ensureHomeAddresses();
  // Not awaited: withRetry (item 2) has no retry-count ceiling by design,
  // so a call that can structurally never succeed (see
  // ensureAccountBalances/tokenAddressFor's comments — discovered the hard
  // way: mainnet contract addresses don't exist on a testnet RPC) would
  // otherwise hang unlock/account-creation indefinitely, not just take a
  // while. Balances/history populate in the background; Home already shows
  // a per-row "updating…" state (balancesLoading) while that happens.
  ensureAccountBalances();
  ensureRecentTransactions(); // depends on homeAddresses, awaited just above
}

// Used by app.js's "Wallet features unavailable — worker failed to start"
// banner's Retry button, which can appear either before or after unlock —
// picks the matching recovery action for whichever phase workerConnected
// went false in, rather than assuming one or the other.
export async function retryWorkerConnection() {
  const { vaultKey } = getSessionVault();
  if (vaultKey) await loadWalletForSession();
  else await checkWorkerConnectivity();
}

// Hiding an already-revealed key needs no re-auth; revealing one does
// (see requestReveal/submitConfirmReveal below) — CLAUDE.md requires a
// confirmation/auth step before key material reaches the main thread.
export function togglePrivateKey(id) {
  const s = getState();
  const key = s.privateKeys.find((k) => k.id === id);
  if (!key) return;
  if (key.revealed) {
    setState((st) => ({ privateKeys: st.privateKeys.map((k) => (k.id === id ? { ...k, revealed: false, words: null } : k)) }));
    return;
  }
  requestReveal(id);
}

export function requestReveal(id) {
  setState({ showConfirmReveal: true, confirmRevealPurpose: 'revealKey', confirmRevealKeyId: id, confirmRevealCode: '', confirmRevealError: '' });
}
export function cancelConfirmReveal() {
  setState({
    showConfirmReveal: false, confirmRevealPurpose: 'revealKey', confirmRevealPayload: null,
    confirmRevealKeyId: null, confirmRevealCode: '', confirmRevealError: ''
  });
}
export function setConfirmRevealCode(v) {
  setSilent({ confirmRevealCode: v.replace(/\D/g, '').slice(0, 6), confirmRevealError: '' });
}
// Re-auth via the same vault the app unlocks with (CLAUDE.md: reveal
// requires explicit confirmation) — a successful unwrap of the vault key
// IS the check; there's no separate verifier to keep in sync with it.
// Doesn't need the vault key itself beyond that proof, so it isn't stored
// anywhere — this re-check is deliberately independent of whatever vault
// key the current session already holds from unlocking.
//
// feature-harden.md 6.3 generalized this from "reveal a key" only to a
// shared re-auth gate (confirmRevealPurpose) also used by recipient
// add/edit/delete — one passcode-confirmation modal/flow to keep
// correct, not three near-duplicate ones.
async function revealAfterVaultCheck(vaultKeyOrNull) {
  if (!vaultKeyOrNull) {
    setState({ confirmRevealError: translate('errors.incorrectPasscode') });
    return;
  }
  const s = getState();
  const purpose = s.confirmRevealPurpose || 'revealKey';

  if (purpose === 'saveRecipient') {
    commitSaveRecipient();
    setState({ showConfirmReveal: false, confirmRevealPurpose: 'revealKey', confirmRevealCode: '', confirmRevealError: '' });
    return;
  }
  if (purpose === 'deleteRecipient') {
    commitDeleteRecipient(s.confirmRevealPayload.recipientId);
    setState({ showConfirmReveal: false, confirmRevealPurpose: 'revealKey', confirmRevealPayload: null, confirmRevealCode: '', confirmRevealError: '' });
    return;
  }
  if (purpose === 'moveLimitOverride') {
    commitMoveLimitOverride();
    setState({ showConfirmReveal: false, confirmRevealPurpose: 'revealKey', confirmRevealCode: '', confirmRevealError: '' });
    return;
  }
  if (purpose === 'newRecipientOverride') {
    commitNewRecipientOverride();
    setState({ showConfirmReveal: false, confirmRevealPurpose: 'revealKey', confirmRevealCode: '', confirmRevealError: '' });
    return;
  }

  const id = s.confirmRevealKeyId;
  let words;
  try {
    ({ words } = await walletCall('revealKey', { id }));
  } catch {
    setState({ showConfirmReveal: false, confirmRevealPurpose: 'revealKey', confirmRevealKeyId: null, confirmRevealCode: '', confirmRevealError: '' });
    showToast(translate('toast.couldntRevealKey'));
    return;
  }
  setState((st) => ({
    privateKeys: st.privateKeys.map((k) => (k.id === id ? { ...k, revealed: true, words } : k)),
    showConfirmReveal: false, confirmRevealPurpose: 'revealKey', confirmRevealKeyId: null, confirmRevealCode: '', confirmRevealError: ''
  }));
}

/* ---- Guided backup ceremony (feature-harden.md CCSS-BR) ---------------
 * Challenges the user on 3 random word positions from an already-revealed
 * phrase, to confirm they actually wrote it down correctly. Every entry
 * point here is skippable by design (user instruction, 2026-08-05): it
 * never blocks reveal/hide or anything else in the app, and
 * backupVerified/backupPromptDismissed are purely informational, never
 * checked by any gate elsewhere.
 */
// CSPRNG, not Math.random, everywhere (CLAUDE.md/feature-harden.md ASVS-6/8)
// -- same technique as workers/retry.js's secureRandomFraction, duplicated
// rather than imported since that module lives under src/workers/ for a
// different reason (it's the worker's own backoff jitter) and this runs on
// the main thread.
function secureRandomFraction() {
  return crypto.getRandomValues(new Uint32Array(1))[0] / 2 ** 32;
}
function randomWordIndices(count, max) {
  const pool = Array.from({ length: max }, (_, i) => i + 1);
  const picked = [];
  while (picked.length < count && pool.length) {
    const i = Math.floor(secureRandomFraction() * pool.length);
    picked.push(pool.splice(i, 1)[0]);
  }
  return picked.sort((a, b) => a - b);
}

export function requestVerifyBackup(keyId) {
  const key = getState().privateKeys.find((k) => k.id === keyId);
  if (!key || !key.words || !key.words.length) return;
  const indices = randomWordIndices(3, key.words.length);
  setState({
    showVerifyBackup: true, verifyBackupKeyId: keyId, verifyBackupIndices: indices,
    verifyBackupAnswers: indices.map(() => ''), verifyBackupError: ''
  });
}

// Inline "not now" next to the reveal screen's offer -- dismisses the
// prompt itself, without even opening the challenge modal.
export function dismissBackupPrompt(keyId) {
  setState((st) => ({ privateKeys: st.privateKeys.map((k) => (k.id === keyId ? { ...k, backupPromptDismissed: true } : k)) }));
}

export function setVerifyBackupAnswer(slot, value) {
  setSilent((st) => {
    const answers = [...st.verifyBackupAnswers];
    answers[slot] = value;
    return { verifyBackupAnswers: answers, verifyBackupError: '' };
  });
}

// The challenge modal's own "Skip for now" -- always available, closes
// without marking the key verified.
export function skipVerifyBackup() {
  setState({
    showVerifyBackup: false, verifyBackupKeyId: null, verifyBackupIndices: [],
    verifyBackupAnswers: ['', '', ''], verifyBackupError: ''
  });
}

export function submitVerifyBackup() {
  const s = getState();
  const key = s.privateKeys.find((k) => k.id === s.verifyBackupKeyId);
  if (!key || !key.words) { skipVerifyBackup(); return; }
  const allCorrect = s.verifyBackupIndices.every((wordNum, slot) => {
    const answer = (s.verifyBackupAnswers[slot] || '').trim().toLowerCase();
    const actual = (key.words[wordNum - 1] || '').trim().toLowerCase();
    return answer !== '' && answer === actual;
  });
  if (!allCorrect) {
    setState({ verifyBackupError: translate('modals.verifyBackup.error') });
    return;
  }
  setState((st) => ({
    privateKeys: st.privateKeys.map((k) => (k.id === key.id ? { ...k, backupVerified: true } : k)),
    showVerifyBackup: false, verifyBackupKeyId: null, verifyBackupIndices: [],
    verifyBackupAnswers: ['', '', ''], verifyBackupError: ''
  }));
  showToast(translate('toast.backupVerified'));
}

export async function submitConfirmReveal() {
  const s = getState();
  const profile = s.profiles.find((p) => p.id === s.activeProfileId) || s.profiles[0];
  let vaultKey = null;
  if (profile) {
    try { vaultKey = await unwrapWithPin(s.confirmRevealCode, profile.vault.pin); } catch { /* wrong PIN */ }
  }
  await revealAfterVaultCheck(vaultKey);
}
export async function confirmRevealWithWebauthn() {
  const s = getState();
  const profile = s.profiles.find((p) => p.id === s.activeProfileId) || s.profiles[0];
  if (!profile || !profile.vault.webauthn) return;
  webauthnPromptActive = true;
  let vaultKey;
  try {
    vaultKey = await unwrapWithWebauthn(profile.vault.webauthn).catch(() => null);
  } finally {
    webauthnPromptActive = false;
  }
  if (!vaultKey) { setState({ confirmRevealError: translate('errors.biometricFailed') }); return; }
  await revealAfterVaultCheck(vaultKey);
}

export function toggleRecoveryInfo() {
  setState((s) => ({ showRecoveryInfo: !s.showRecoveryInfo }));
}

export function openImportKey() {
  setState({ showImportKey: true, importText: '', importError: '' });
}
export function cancelImportKey() {
  setState({ showImportKey: false });
}
// feature-harden.md 6.9: the length check stays as a cheap first pass
// (no point calling into the worker for an obviously-too-short phrase),
// but a phrase of plausible length now also gets checked against the
// real BIP-39 wordlist/checksum (tether.worker.js's validateSeedPhrase,
// the same validator importKey's own real accept/reject already uses) —
// not a second, hand-rolled validator. Words that aren't real BIP-39
// entries, or a bad checksum, get their own distinct error message
// rather than being silently accepted by a check that only ever looked
// at word count.
export async function validateImportPhraseBlur() {
  const text = getState().importText.trim();
  if (!text) { setState({ importError: '' }); return; }
  const words = text.split(/\s+/).filter(Boolean);
  if (words.length < 12) { setState({ importError: translate('errors.validRecoveryPhrase') }); return; }
  const { valid } = await walletCall('validateSeedPhrase', { words });
  setState({ importError: valid ? '' : translate('errors.notBip39Phrase') });
}
export async function saveImportKey() {
  const words = getState().importText.trim().split(/\s+/).filter(Boolean);
  if (words.length < 12) { setState({ importError: translate('errors.validRecoveryPhrase') }); return; }
  const n = getState().privateKeys.length + 1;
  const label = translate('settings.importedKeyLabel') + (n > 1 ? ' ' + n : '');
  try {
    await walletCall('importKey', { words, label });
  } catch (err) {
    setState({ importError: err.message || translate('errors.importFailed') });
    return;
  }
  await refreshKeys();
  setState({ showImportKey: false });
  showToast(translate('toast.keyImported'));
}

/* ---- Lock ------------------------------------------------------------ */
export function goLock() {
  haptic();
  clearSessionVault();
  // Clears the worker's real seed/key material from memory (CLAUDE.md:
  // "clear sensitive in-memory state on lock"). Not awaited — locking must
  // feel instant, and this is a trivial in-worker Map.clear(); best-effort
  // if the worker is somehow unreachable, since the seed stays encrypted
  // at rest regardless and reloads correctly from loadWalletForSession on
  // the next unlock either way.
  walletCall('clearKeys').catch(() => {});
  // Clears sensitive in-memory UI state on lock, per CLAUDE.md. savedRecipients
  // and wallets are both encrypted at rest, but the *decrypted* copy held
  // in state while unlocked is cleared here same as privateKeys' revealed
  // words — otherwise the next profile to unlock on this device would
  // briefly render the previous profile's account list/balances until
  // loadWalletsForSession overwrites it.
  setState((s) => ({
    screen: 'landing',
    landingFormOpen: false,
    landingAddingProfile: false,
    lockCode: '',
    lockError: '',
    savedRecipients: [],
    wallets: [],
    privateKeys: s.privateKeys.map((k) => ({ ...k, revealed: false, words: null })),
    // verifyBackupAnswers can hold typed-out mnemonic words mid-ceremony --
    // cleared here for the same reason privateKeys' revealed words are.
    showVerifyBackup: false, verifyBackupKeyId: null, verifyBackupIndices: [],
    verifyBackupAnswers: ['', '', ''], verifyBackupError: ''
  }));
}
export function setLockCode(v) {
  // setSilent, not setState: a full re-render on every digit would tear
  // down and recreate the <input>, dismissing the on-screen keyboard
  // after each keystroke on mobile.
  setSilent({ lockCode: v.replace(/\D/g, '').slice(0, 6), lockError: '' });
}
// feature-harden.md 6.1: throttled with a persisted, per-profile
// exponential backoff (profileSecurity/backoffMsFor below) — real
// unwrapWithPin failures only, never the pre-submit format validation
// setLockCode already does (non-digit stripping/6-char cap), per the
// doc's explicit "do not throttle on client-side format validation".
export async function unlock() {
  if (getState().lockChecking) return;
  const s = getState();
  const profile = s.profiles.find((p) => p.id === s.activeProfileId) || s.profiles[0];
  if (!profile) { setState({ lockError: translate('errors.noAccountFound') }); return; }

  // Enforced here, not just via the disabled unlock button — a locked-out
  // profile must reject an unwrap attempt even if something else (a stale
  // render, a direct call) got past the UI gate.
  const sec = profileSecurity(profile);
  if (sec.lockoutUntil && sec.lockoutUntil > Date.now()) {
    const seconds = Math.ceil((sec.lockoutUntil - Date.now()) / 1000);
    setState({ lockError: translate('errors.tooManyAttempts', { seconds }) });
    return;
  }

  setState({ lockChecking: true, lockError: '' });
  let vaultKey = null;
  try {
    vaultKey = await unwrapWithPin(getState().lockCode, profile.vault.pin);
  } catch { /* wrong PIN — AES-GCM auth-tag failure, treated as incorrect below */ }

  if (vaultKey) {
    const profiles = getState().profiles.map((p) => (p.id === profile.id
      ? { ...p, security: { ...profileSecurity(p), failedAttempts: 0, lockoutUntil: null } }
      : p));
    persistProfiles(profiles);
    setSessionVault(profile.id, vaultKey);
    await loadRecipientsForSession();
    await loadWalletForSession();
    setState({ profiles, lockCode: '', lockChecking: false });
    applyPendingScreen();
    return;
  }

  const failedAttempts = sec.failedAttempts + 1;
  if (sec.wipeAfterN > 0 && failedAttempts >= sec.wipeAfterN) {
    setState({ lockChecking: false });
    wipeProfile(profile.id);
    return;
  }
  // backoffMsFor returns 0 for the first two failures — lockoutUntil must
  // stay null (not Date.now()) then, or "no lockout yet" would read as
  // a real, already-expired timestamp instead of the actual off-state.
  const backoffMs = backoffMsFor(failedAttempts);
  const lockoutUntil = backoffMs > 0 ? Date.now() + backoffMs : null;
  const profiles = getState().profiles.map((p) => (p.id === profile.id
    ? { ...p, security: { ...sec, failedAttempts, lockoutUntil } }
    : p));
  persistProfiles(profiles);
  setState({ profiles, lockError: translate('errors.incorrectPasscode'), lockChecking: false });
}

// Biometric unlock — a convenience layer over the same vault the PIN path
// unwraps (see vault.js); any failure here (cancelled prompt, no
// matching authenticator, PRF unavailable this time) falls back to the
// always-present PIN UI rather than surfacing an error of its own, since
// PIN entry is still right there on the same screen.
export async function unlockWithWebauthn() {
  if (getState().lockChecking) return;
  const s = getState();
  const profile = s.profiles.find((p) => p.id === s.activeProfileId) || s.profiles[0];
  if (!profile || !profile.vault.webauthn) return;

  setState({ lockChecking: true, lockError: '' });
  webauthnPromptActive = true;
  let vaultKey;
  try {
    vaultKey = await unwrapWithWebauthn(profile.vault.webauthn).catch(() => null);
  } finally {
    webauthnPromptActive = false;
  }

  if (vaultKey) {
    setSessionVault(profile.id, vaultKey);
    await loadRecipientsForSession();
    await loadWalletForSession();
    setState({ lockChecking: false });
    applyPendingScreen();
  } else {
    setState({ lockChecking: false, lockError: 'Biometric unlock failed — enter your passcode' });
  }
}

/* ---- Landing / onboarding / profiles ---------------------------------- */
function persistProfiles(list) {
  try { localStorage.setItem('tynti_profiles', JSON.stringify(list)); } catch { /* storage unavailable */ }
}
function persistActiveProfile(id) {
  try { localStorage.setItem('tynti_active_profile', id); } catch { /* storage unavailable */ }
}

// feature-harden.md 6.1: lock-screen attempt throttling [ASVS-3]. Persisted
// on the profile itself (survives reload — an attacker with the device
// can't just reload past it) rather than in-memory state.
// profileSecurity() defaults pre-existing profiles (created before this
// field existed) to "never throttled yet" rather than crashing on a
// missing field.
function profileSecurity(profile) {
  return (profile && profile.security) || { failedAttempts: 0, lockoutUntil: null, wipeAfterN: 0 };
}
// No backoff for the first two attempts (a fat-fingered typo shouldn't
// lock anyone out); doubles from there, capped at 5 minutes. Pure/exported
// so it's unit-testable without going through the real PIN-unwrap crypto.
const BACKOFF_FREE_ATTEMPTS = 2;
const BACKOFF_BASE_MS = 5000;
const BACKOFF_CAP_MS = 5 * 60 * 1000;
export function backoffMsFor(failedAttempts) {
  if (failedAttempts <= BACKOFF_FREE_ATTEMPTS) return 0;
  return Math.min(BACKOFF_BASE_MS * 2 ** (failedAttempts - BACKOFF_FREE_ATTEMPTS - 1), BACKOFF_CAP_MS);
}
// Opt-in only — 0 means disabled, never destructive by default. A user who
// enables this is explicitly trading "an attacker who guesses N times wipes
// this profile's local data" against "a lost/stolen device with the app
// left onboarded can't be brute-forced indefinitely."
export const WIPE_AFTER_OPTIONS = Object.freeze([0, 5, 10, 20]);
export function setWipeAfterN(n) {
  const v = WIPE_AFTER_OPTIONS.includes(n) ? n : 0;
  const s = getState();
  const profiles = s.profiles.map((p) => (p.id === s.activeProfileId
    ? { ...p, security: { ...profileSecurity(p), wipeAfterN: v } }
    : p));
  setState({ profiles });
  persistProfiles(profiles);
}
// Wipes exactly this profile's local data (its own encrypted seed/
// recipients/wallets, plus the profile entry itself) — never another
// profile's, since each profile's data is already namespaced by id (same
// scoping DECISIONS.md's 2026-08-02 entry established for wallets/
// recipients). No in-worker key material to clear here: a wipe only ever
// fires from a failed *unlock* attempt, before any vault key for this
// profile was ever obtained, so the worker never held it.
function wipeProfile(profileId) {
  const s = getState();
  const profiles = s.profiles.filter((p) => p.id !== profileId);
  persistProfiles(profiles);
  try {
    localStorage.removeItem('tynti_seed_enc_' + profileId);
    localStorage.removeItem('tynti_recipients_enc_' + profileId);
    localStorage.removeItem('tynti_wallets_enc_' + profileId);
  } catch { /* storage unavailable */ }
  const activeProfileId = s.activeProfileId === profileId ? (profiles[0] ? profiles[0].id : null) : s.activeProfileId;
  // Explicit removeItem when there's no replacement — not just skipping
  // the write — so a wipe never leaves tynti_active_profile pointing at a
  // profile id that no longer exists in tynti_profiles. Caught via
  // advisor() review: harmless today (loadProfiles/loadActiveProfileId
  // don't cross-validate, and landing.js's `profiles.length > 0` check
  // means an empty profiles list already shows the create-account intro
  // regardless of what's in this key), but "wipe" should mean no trace
  // left behind, not "left behind but happens not to matter yet."
  if (activeProfileId) persistActiveProfile(activeProfileId);
  else { try { localStorage.removeItem('tynti_active_profile'); } catch { /* storage unavailable */ } }
  setState({ profiles, activeProfileId, lockCode: '', lockError: translate('errors.profileWiped') });
}

export function openLandingForm() {
  setState({
    landingFormOpen: true,
    newProfileName: '', newProfileEmail: '', newProfileAnonymous: false, newProfileNameBeforeAnon: '',
    newProfileCode: '', newProfileCodeConfirm: '', agreeTerms: false, agreeSelfCustody: false,
    newProfileErrors: {}
  });
}

export function setNewProfileName(v) {
  setSilent((s) => ({ newProfileName: v, newProfileErrors: { ...s.newProfileErrors, name: null } }));
}
export function setNewProfileEmail(v) {
  setSilent((s) => ({ newProfileEmail: v, newProfileErrors: { ...s.newProfileErrors, email: null } }));
}
// Anonymous forces Name to "Anon" and clears Email (both fields grey out via
// `disabled` in the render layer — no inline styles, CSP forbids those).
// The typed name is snapshotted so un-checking restores it instead of
// losing what the user already entered.
export function toggleNewProfileAnonymous() {
  setState((s) => {
    if (!s.newProfileAnonymous) {
      return {
        newProfileAnonymous: true,
        newProfileNameBeforeAnon: s.newProfileName,
        newProfileName: translate('landing.anonName'),
        newProfileEmail: '',
        newProfileErrors: { ...s.newProfileErrors, name: null, email: null }
      };
    }
    return { newProfileAnonymous: false, newProfileName: s.newProfileNameBeforeAnon || '' };
  });
}
export function setNewProfileCode(v) {
  setSilent((s) => ({ newProfileCode: v.replace(/\D/g, '').slice(0, 6), newProfileErrors: { ...s.newProfileErrors, code: null } }));
}
export function setNewProfileCodeConfirm(v) {
  setSilent((s) => ({ newProfileCodeConfirm: v.replace(/\D/g, '').slice(0, 6), newProfileErrors: { ...s.newProfileErrors, code: null } }));
}
export function toggleAgreeTerms() {
  setState((s) => ({ agreeTerms: !s.agreeTerms, newProfileErrors: { ...s.newProfileErrors, agree: null } }));
}
export function toggleAgreeSelfCustody() {
  setState((s) => ({ agreeSelfCustody: !s.agreeSelfCustody, newProfileErrors: { ...s.newProfileErrors, agree: null } }));
}

// A pop-up (modal), not a full screen — feature-ui.md calls for
// it from several places (Landing's checkboxes, its FAQ, Status Panel)
// without navigating away from wherever it was opened.
export function openLicense() {
  setState({ showLicense: true });
}
export function closeLicense() {
  setState({ showLicense: false });
}

// Blur-time checks only verify *format*, matching the rest of the app's
// inline-validation convention (send/recipient/import-key blur handlers) —
// required-ness is a submit-time-only check so an untouched field doesn't
// show an error before the user has typed anything.
// setSilent, not setState: this form has two adjacent fields (code ->
// confirm) where blurring one to focus the next is the normal flow. A
// setState here fully clears+rebuilds the DOM (see app.js's render()) —
// doing that mid-transition races the browser's focus handoff into the
// next field and can silently drop the keystrokes typed there. setSilent
// still updates state immediately; the error just renders on whatever
// state change happens next (the next field's own edit, or submit).
export function validateNewProfileEmailBlur() {
  const s = getState();
  const email = s.newProfileEmail.trim();
  if (!email) { setSilent((st) => ({ newProfileErrors: { ...st.newProfileErrors, email: null } })); return; }
  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  setSilent((st) => ({ newProfileErrors: { ...st.newProfileErrors, email: valid ? null : translate('errors.validEmail') } }));
}
export function validateNewProfileCodeBlur() {
  const s = getState();
  if (!s.newProfileCode) { setSilent((st) => ({ newProfileErrors: { ...st.newProfileErrors, code: null } })); return; }
  const valid = /^\d{6}$/.test(s.newProfileCode);
  setSilent((st) => ({ newProfileErrors: { ...st.newProfileErrors, code: valid ? null : translate('errors.passcodeDigits') } }));
}
export function validateNewProfileCodeConfirmBlur() {
  const s = getState();
  if (!s.newProfileCodeConfirm) { setSilent((st) => ({ newProfileErrors: { ...st.newProfileErrors, code: null } })); return; }
  const valid = s.newProfileCode === s.newProfileCodeConfirm;
  setSilent((st) => ({ newProfileErrors: { ...st.newProfileErrors, code: valid ? null : translate('errors.passcodesNoMatch') } }));
}

function validateNewProfile(s) {
  const errors = {};
  if (!s.newProfileAnonymous && !s.newProfileName.trim()) errors.name = translate('errors.enterNameOrCompany');
  if (s.newProfileEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.newProfileEmail.trim())) errors.email = translate('errors.validEmail');
  if (!/^\d{6}$/.test(s.newProfileCode)) errors.code = translate('errors.enter6DigitPasscode');
  else if (s.newProfileCode !== s.newProfileCodeConfirm) errors.code = translate('errors.passcodesNoMatch');
  if (!s.agreeTerms || !s.agreeSelfCustody) errors.agree = translate('errors.agreeBothStatements');
  return errors;
}

export async function submitOpenAccount() {
  if (getState().newProfileSubmitting) return;
  const s = getState();
  const errors = validateNewProfile(s);
  if (Object.keys(errors).length) { setState({ newProfileErrors: errors }); return; }

  setState({ newProfileSubmitting: true });

  const profileName = s.newProfileAnonymous ? translate('landing.anonName') : s.newProfileName.trim();
  let vaultKey, pinWrap;
  try {
    vaultKey = await generateVaultKey();
    pinWrap = await createPinWrap(s.newProfileCode, vaultKey);
  } catch {
    setState({ newProfileSubmitting: false, newProfileErrors: { agree: translate('errors.passcodeSetupFailed') } });
    return;
  }
  // Silent no-op (not an error) when WebAuthn/prf isn't available or the
  // user declines the platform prompt — the PIN wrap above is already a
  // fully secure, fully functional vault on its own (see vault.js).
  webauthnPromptActive = true;
  let webauthnWrap;
  try {
    webauthnWrap = await registerWebauthn(profileName, vaultKey).catch(() => null);
  } finally {
    webauthnPromptActive = false;
  }

  const profile = {
    id: 'p' + Date.now(),
    name: profileName,
    email: s.newProfileAnonymous ? '' : s.newProfileEmail.trim(),
    anonymous: s.newProfileAnonymous,
    avatar: CONFIG.AVATARS[0], // circleUserRound — default; changeable afterward in Settings
    vault: { pin: pinWrap, webauthn: webauthnWrap },
    createdAt: new Date().toISOString(),
    // feature-harden.md 6.1: per-profile failed-attempt throttling state.
    // wipeAfterN 0 = disabled (opt-in) — never destructive by default.
    security: { failedAttempts: 0, lockoutUntil: null, wipeAfterN: 0 }
  };
  const profiles = [...getState().profiles, profile];
  persistProfiles(profiles);
  persistActiveProfile(profile.id);
  setSessionVault(profile.id, vaultKey);
  // profiles must land in state before loadWalletForSession, which looks
  // the new profile up via getState().profiles to read/attach walletKeyId.
  setState({ profiles });
  await loadRecipientsForSession(); // always [] for a brand-new profile, but keeps state consistent with the unlock paths
  await loadWalletForSession();
  setState({
    activeProfileId: profile.id,
    screen: 'home', landingFormOpen: false, landingAddingProfile: false,
    newProfileSubmitting: false,
    newProfileName: '', newProfileEmail: '', newProfileAnonymous: false, newProfileNameBeforeAnon: '',
    newProfileCode: '', newProfileCodeConfirm: '', agreeTerms: false, agreeSelfCustody: false, newProfileErrors: {}
  });
  showToast(webauthnWrap ? translate('toast.accountOpenedBiometric') : translate('toast.accountOpened'));
}
